import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {bindWaiseSnapshot,unpackWaiseSnapshot,prepareWaiseCloudMutation,waiseCommandAvailable,waisePendingMutation} from '../../utils/waiseCloudSnapshot';
import { useLocation, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowLeft,
  BarChart3,
  Boxes,
  CalendarRange,
  CheckCircle2,
  ClipboardCheck,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Download,
  Loader2,
  Minus,
  PackagePlus,
  PackageSearch,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  ShieldCheck,
  ShoppingCart,
  Settings,
  Trash2,
  Truck,
  WifiOff,
  X,
} from "lucide-react";
import { showToast } from "../../utils/toast";
import {
  buildCloudLoginUrl,
  clearInvalidCloudSession,
  isRemoteCloudHost,
} from "../../utils/cloudBrowserSession";
import {
  findGeneralSupplierId,
  normalizeOptionalPackaging,
} from "../../utils/waiseStockItemForm";
import "./WaiseV2.css";

const SECTIONS = [
  { id: "today", label: "Aujourd'hui", mobileLabel: "Aujourd'hui", icon: PackageSearch, endpoint: "/api/waise/v2/today" },
  { id: "situation", label: "Situation", icon: CalendarRange, endpoint: "/api/waise/v2/daily-situation", requiresDailySituation: true },
  { id: "analysis", label: "Analyse", icon: BarChart3, endpoint: "/api/waise/v2/analysis" },
  { id: "stock", label: "Stock", icon: Boxes, endpoint: "/api/waise/v2/stock?limit=200" },
  { id: "purchases", label: "Achats", icon: ArrowDownToLine, endpoint: "/api/waise/v2/purchases?limit=100" },
  { id: "losses", label: "Pertes", icon: Trash2, endpoint: "/api/waise/v2/losses?limit=200" },
  { id: "inventories", label: "Inventaires", icon: ClipboardCheck, endpoint: "/api/waise/v2/inventories?limit=100" },
  { id: "configuration", label: "Configuration", mobileLabel: "Config", icon: Settings, endpoint: "/api/waise/v2/configuration" },
];

const CONFIGURATION_SUBSECTIONS = [
  { id: "resold", label: "Produits revendus" },
  { id: "ingredients", label: "Ingrédients" },
  { id: "recipes", label: "Recettes" },
  { id: "suppliers", label: "Fournisseurs" },
  { id: "losses", label: "Réglages des pertes" },
  { id: "warehouses", label: "Lieux de stockage" },
];

const STATUS_LABELS = {
  negative: "Stock incohérent",
  out_of_stock: "Rupture",
  low_stock: "Stock faible",
  not_initialized: "À initialiser",
  ok: "Correct",
  draft: "Brouillon",
  sent: "Envoyé",
  expected: "Attendu",
  overdue: "En retard",
  partial: "Partiel",
  received: "Reçu",
  stored: "Rangé",
  completed: "Terminé",
  in_progress: "En cours",
  planned: "Prévu",
  cancelled: "Annulé",
};

const safeJson = (value, fallback = {}) => {
  try {
    return JSON.parse(value || "");
  } catch (_error) {
    return fallback;
  }
};

const formatNumber = (value) => new Intl.NumberFormat("fr-FR", {
  maximumFractionDigits: 2,
}).format(Number(value) || 0);

const formatMoney = (value) => `${new Intl.NumberFormat("fr-FR", {
  maximumFractionDigits: 0,
}).format(Number(value) || 0)} F`;

const dailyMovementSummary = (row) => [
  row.entries ? `Entrées +${formatNumber(row.entries)}` : null,
  row.outputs ? `Sorties −${formatNumber(row.outputs)}` : null,
  row.losses ? `Pertes −${formatNumber(row.losses)}` : null,
  row.production ? `Production +${formatNumber(row.production)}` : null,
  row.transfers ? `Transferts −${formatNumber(row.transfers)}` : null,
  row.adjustments ? `Ajustement ${row.adjustments > 0 ? "+" : ""}${formatNumber(row.adjustments)}` : null,
].filter(Boolean).join(" · ") || "Aucun mouvement";

const exportDailySituationWorkbook = async (data) => {
  const xlsx = await import("xlsx");
  const rows = data?.rows || [];
  const sheet = xlsx.utils.aoa_to_sheet([
    ["Situation journalière WAISE"],
    ["Journée", data?.business_date || ""],
    ["Période", data?.period?.timeWindowLabel || ""],
    ["Valorisation", data?.summary?.valuation_complete ? "Complète" : "Incomplète"],
    [],
    ["Article", "Catégorie", "Lieu", "Unité", "Stock initial", "Entrées", "Sorties", "Pertes", "Production", "Transferts", "Ajustements", "Stock final", "Valeur initiale", "Valeur finale"],
    ...rows.map((row) => [
      row.item_name, row.category, row.warehouse_name, row.unit,
      row.opening_quantity, row.entries, row.outputs, row.losses,
      row.production, row.transfers, row.adjustments, row.closing_quantity,
      row.opening_value == null ? "Non valorisée" : row.opening_value,
      row.closing_value == null ? "Non valorisée" : row.closing_value,
    ]),
  ]);
  sheet["!cols"] = [{ wch: 30 }, { wch: 20 }, { wch: 22 }, { wch: 10 }, ...Array.from({ length: 10 }, () => ({ wch: 15 }))];
  const workbook = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(workbook, sheet, "Situation");
  xlsx.writeFile(workbook, `waise-situation-${data?.business_date || "jour"}.xlsx`);
};

const printDailySituation = (data) => {
  const escape = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]));
  const rows = (data?.rows || []).map((row) => `<tr><td>${escape(row.item_name)}</td><td>${escape(row.warehouse_name)}</td><td>${escape(formatNumber(row.opening_quantity))}</td><td>${escape(dailyMovementSummary(row))}</td><td>${escape(formatNumber(row.closing_quantity))}</td><td>${escape(row.closing_value == null ? "Non valorisée" : formatMoney(row.closing_value))}</td></tr>`).join("");
  const popup = window.open("", "_blank");
  if (!popup) throw new Error("Autorisez l'ouverture de la fenêtre d'impression.");
  popup.opener = null;
  popup.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Situation ${escape(data?.business_date)}</title><style>body{font-family:Arial,sans-serif;padding:24px;color:#111}h1{font-size:22px;margin:0 0 5px}p{margin:0 0 18px;color:#555}table{width:100%;border-collapse:collapse;font-size:11px}th,td{border-bottom:1px solid #ddd;padding:8px 6px;text-align:left}th{background:#f2f2f2}.warning{padding:9px;background:#fff3cd;margin-bottom:14px}@media print{body{padding:0}}</style></head><body><h1>Situation journalière WAISE</h1><p>${escape(data?.period?.label || data?.business_date)} · ${escape(data?.period?.timeWindowLabel || "")}</p>${data?.summary?.valuation_complete ? "" : '<div class="warning">Valorisation incomplète : certains prix d’achat sont absents.</div>'}<table><thead><tr><th>Article</th><th>Lieu</th><th>Initial</th><th>Mouvements</th><th>Final</th><th>Valeur finale</th></tr></thead><tbody>${rows}</tbody></table><script>window.onload=()=>window.print();</script></body></html>`);
  popup.document.close();
};

const PaginatedListFooter = ({ page, pageSize, total, onPageChange, onPageSizeChange }) => {
  if (!total) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  return <div className="wv2-list-pagination">
    <label>Afficher <select value={pageSize} onChange={(event) => onPageSizeChange(Number(event.target.value))} aria-label="Nombre d'éléments par page"><option value="10">10</option><option value="25">25</option><option value="50">50</option></select> éléments</label>
    <span>{first}–{last} sur {total}</span>
    <div><button type="button" className="wv2-secondary" onClick={() => onPageChange(page - 1)} disabled={page <= 1} aria-label="Page précédente"><ChevronLeft size={17} /></button><span>Page {page} sur {totalPages}</span><button type="button" className="wv2-secondary" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} aria-label="Page suivante"><ChevronRight size={17} /></button></div>
  </div>;
};

const stockNatureLabel = (item) => {
  const kind = String(item?.inventory_kind || item?.linked_inventory_kind || "").toLowerCase();
  const type = String(item?.type || "").toLowerCase();
  if (kind === "resold" || type === "resold") return "Produit revendu";
  if (kind === "consumable" || ["consumable", "packaging", "cleaning"].includes(type)) return "Consommable";
  return "Ingrédient";
};

const stockIdentityLabel = (item) => {
  const link = item?.cash_register_link;
  if (!link?.variation_id && !item?.variation_id && !item?.linked_variation_name) return null;
  return link?.variation_name || item?.linked_variation_name || "Déclinaison";
};

const stockIdentitySuffix = (item, prefix = "Déclinaison : ") => {
  const identity = stockIdentityLabel(item);
  if (!identity) return "";
  const normalizedName = String(item?.name || "").trim().toLocaleLowerCase("fr");
  const normalizedIdentity = String(identity).trim().toLocaleLowerCase("fr");
  if (normalizedIdentity && normalizedName.includes(normalizedIdentity)) return "";
  return ` · ${prefix}${identity}`;
};

const showWaiseToast = (type, message) => showToast(type, message, {
  position: "bottom-right",
});

const pendingCloudCommands = new Map();
let cloudAuthRedirectStarted = false;

const handleInvalidCloudSession = (response) => {
  if (response?.status !== 401 || typeof window === "undefined") return false;
  if (!isRemoteCloudHost(window.location?.hostname)) return false;
  clearInvalidCloudSession(window.localStorage);
  if (!cloudAuthRedirectStarted) {
    cloudAuthRedirectStarted = true;
    window.location.assign(buildCloudLoginUrl(window.location));
  }
  return true;
};

const trackWaiseCloudCommand = (commandUuid) => {
  if (!commandUuid || pendingCloudCommands.has(commandUuid)) return;
  const startedAt = Date.now();
  let timer = null;
  const finish = () => {
    window.clearTimeout(timer);
    pendingCloudCommands.delete(commandUuid);
  };
  const poll = async () => {
    try {
      const response = await fetch(`/api/waise-cloud/commands/${encodeURIComponent(commandUuid)}`, {
        headers: authHeaders(),
      });
      const command = await response.json().catch(() => ({}));
      if (handleInvalidCloudSession(response)) {
        finish();
        return;
      }
      if (!response.ok) throw new Error(command.error || "Suivi de la synchronisation indisponible.");
      if (command.status === "applied") {
        finish();
        showWaiseToast("success", "Action appliquée par le serveur local.");
        window.dispatchEvent(new CustomEvent("waise-cloud-command-resolved", { detail: command }));
        return;
      }
      if (["rejected", "failed", "cancelled", "blocked_incompatible"].includes(command.status)) {
        finish();
        const label = command.status === "blocked_incompatible"
          ? "Serveur local à mettre à jour."
          : command.error_message || "L'action n'a pas été appliquée.";
        showWaiseToast("error", label);
        window.dispatchEvent(new CustomEvent("waise-cloud-command-resolved", { detail: command }));
        return;
      }
    } catch {
      // L'action reste visible comme en attente. Une coupure réseau ne doit pas
      // transformer une absence de réponse en échec métier.
    }
    if (Date.now() - startedAt >= 5 * 60 * 1000) {
      finish();
      showWaiseToast("info", "Action toujours en attente de synchronisation locale.");
      return;
    }
    timer = window.setTimeout(poll, 5000);
  };
  pendingCloudCommands.set(commandUuid, finish);
  timer = window.setTimeout(poll, 2000);
};

const waitForWaiseCloudCommand = async (commandUuid, timeoutMs = 30 * 1000) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const response = await fetch(`/api/waise-cloud/commands/${encodeURIComponent(commandUuid)}`, {
      headers: authHeaders(),
    });
    const command = await response.json().catch(() => ({}));
    if (handleInvalidCloudSession(response)) {
      throw new Error("Votre session WAISE Cloud a expiré. Reconnexion en cours…");
    }
    if (!response.ok) throw new Error(command.error || "Suivi de la synchronisation indisponible.");
    if (command.status === "applied") {
      window.dispatchEvent(new CustomEvent("waise-cloud-command-resolved", { detail: command }));
      return bindWaiseSnapshot(command.result?.result || command.result || {},command.result?.revisions);
    }
    if (["rejected", "failed", "cancelled", "blocked_incompatible"].includes(command.status)) {
      throw Object.assign(new Error(command.error_message || "L'action n'a pas été appliquée."),{
        terminal:command.status!=='blocked_incompatible',code:command.error_code,
        details:command.result?.details,
      });
    }
    await new Promise((resolve) => window.setTimeout(resolve, 1500));
  }
  throw new Error("L'action est toujours en attente du serveur local. Vous pourrez la retrouver dans WAISE après synchronisation.");
};

const waitForPurchaseOrderProjection = async (orderId, timeoutMs = 30000) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    try {
      return await fetchWaise(`/api/waise/purchase-orders/${orderId}`);
    } catch (error) {
      if (error?.code !== "HTTP_404") throw error;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 1000));
  }
  const error = new Error("Le bon est enregistré, mais son PDF est encore en cours de synchronisation. Réessayez dans quelques secondes.");
  error.code = "WAISE_PURCHASE_PROJECTION_PENDING";
  throw error;
};

const formatTime = (value) => {
  if (!value) return "Heure non renseignée";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return "Heure non renseignée";
  return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
};

const statusTone = (status) => {
  if (["negative", "out_of_stock", "overdue"].includes(status)) return "danger";
  if (["low_stock", "not_initialized", "partial", "received", "in_progress"].includes(status)) return "warning";
  if (["ok", "completed", "stored"].includes(status)) return "success";
  return "neutral";
};

const authHeaders = (extra = {}) => {
  const token = localStorage.getItem("moo_cloud_token") || localStorage.getItem("moo_token");
  return {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const fetchWaise = async (url, signal) => {
  const response = await fetch(url, { signal, headers: authHeaders({'X-Waise-Revision-Protocol':'1'}) });
  const body = unpackWaiseSnapshot(await response.json().catch(() => ({})));
  if (handleInvalidCloudSession(response)) {
    const error = new Error("Votre session WAISE Cloud a expiré. Reconnexion en cours…");
    error.code = "CLOUD_SESSION_INVALID";
    throw error;
  }
  if (!response.ok) {
    const error = new Error(body.error || "Waise n'a pas pu charger ces informations.");
    error.code = body.code || `HTTP_${response.status}`;
    throw error;
  }
  if (body?.complete === false) throw new Error("Les données reçues sont incomplètes.");
  return body;
};

const mutateWaise = async (url, { method = "POST", body, awaitCloudResult = false, source } = {}) => {
  const remote=isRemoteCloudHost(window.location?.hostname);
  let headers={};let recovery=null;
  const finish=result=>{
    recovery?.clear();
    if(recovery?.differentIntent) throw new Error("L'action précédente est enregistrée. Actualisez les informations avant de confirmer une nouvelle action.");
    return result;
  };
  if(remote) {
    const prepared=prepareWaiseCloudMutation(url,{method,body,source});
    const actor=safeJson(localStorage.getItem('moo_cloud_user') || localStorage.getItem('moo_user'),{});
    if(!actor.id) throw new Error('Reconnectez-vous à WAISE Cloud pour effectuer cette action.');
    recovery=waisePendingMutation(url,{method,...prepared},`${window.location.host}:${actor.id}`);
    body=recovery.pending.body;
    headers={...recovery.pending.headers,'X-Idempotency-Key':recovery.pending.requestId};
    if(recovery.pending.commandUuid) {
      try {return finish(await waitForWaiseCloudCommand(recovery.pending.commandUuid));}
      catch(error) {if(error.terminal) recovery.clear();else trackWaiseCloudCommand(recovery.pending.commandUuid);throw error;}
    }
  }
  const response = await fetch(url, {
    method,
    headers: authHeaders({ "Content-Type": "application/json",...headers }),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (handleInvalidCloudSession(response)) {
    const error = new Error("Votre session WAISE Cloud a expiré. Reconnexion en cours…");
    error.code = "CLOUD_SESSION_INVALID";
    throw error;
  }
  if (!response.ok) {
    if(payload.not_queued===true || response.status===428) recovery?.clear();
    const error = new Error(payload.error || "Cette opération Waise n'a pas abouti.");
    error.code = payload.code;
    error.details = payload.details;
    throw error;
  }
  if (payload?.queued && payload?.command_uuid) {
    if(recovery) {recovery.pending.commandUuid=payload.command_uuid;recovery.store();}
    showWaiseToast("info", "Action enregistrée · en attente de synchronisation locale.");
    if (awaitCloudResult || remote) {
      try {return finish(await waitForWaiseCloudCommand(payload.command_uuid));}
      catch(error) {if(error.terminal) recovery?.clear();else trackWaiseCloudCommand(payload.command_uuid);throw error;}
    }
    trackWaiseCloudCommand(payload.command_uuid);
  }
  recovery?.clear();
  return payload;
};
const commandAvailable=(type,guarded=false)=>!isRemoteCloudHost(window.location?.hostname) || waiseCommandAvailable(type,guarded);

const makeRequestId = (prefix) => {
  if (window.crypto?.randomUUID) return `${prefix}-${window.crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getDefaultWarehouse = (warehouses = []) => {
  const active = warehouses.filter((entry) => entry?.is_active !== 0);
  return active.find((entry) => Number(entry?.is_main_pos_store) === 1)
    || active.find((entry) => entry?.type === "primary")
    || active.find((entry) => String(entry?.name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() === "entrepot principal")
    || active[0]
    || null;
};

const downloadPurchaseOrderPdf = async (order) => {
  const response = await fetch(`/api/waise/purchase-orders/${order.id}/pdf`, {
    headers: authHeaders({ Accept: "application/pdf" }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error || "Le PDF de ce bon n'a pas pu être préparé.");
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  const disposition = response.headers.get("Content-Disposition");
  const match = disposition && /filename="([^"]+)"/i.exec(disposition);
  link.href = url;
  link.download = match?.[1] || `${order.number || "bon-de-commande"}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const Badge = ({ status, children }) => (
  <span className={`wv2-badge wv2-badge--${statusTone(status)}`}>
    {children || STATUS_LABELS[status] || status || "Information"}
  </span>
);

const Empty = ({ title, note }) => (
  <div className="wv2-empty">
    <CheckCircle2 size={30} />
    <strong>{title}</strong>
    <span>{note}</span>
  </div>
);

const Kpi = ({ label, value, tone = "neutral", note }) => (
  <article className={`wv2-kpi wv2-kpi--${tone}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    {note ? <small>{note}</small> : null}
  </article>
);

const ActionButton = ({ children, onClick, disabled }) => (
  <button type="button" className="wv2-primary" onClick={onClick} disabled={disabled}>
    <Plus size={17} /> {children}
  </button>
);

const WaiseDialog = ({ title, subtitle, children, onClose, busy, error }) => (
  <div className="wv2-dialog-backdrop" role="presentation">
    <section className="wv2-dialog" role="dialog" aria-modal="true" aria-label={title}>
      <header><div><p className="wv2-eyebrow">Action sécurisée</p><h2>{title}</h2>{subtitle ? <p>{subtitle}</p> : null}</div><button type="button" onClick={onClose} disabled={busy} aria-label="Fermer"><X size={21} /></button></header>
      {error ? <div className="wv2-form-error"><AlertTriangle size={17} />{error}</div> : null}
      {children}
    </section>
  </div>
);

const WaiseDrawer = ({ title, eyebrow = "Détail", children, action, actionLabel, onClose, wide = false, busy = false }) => {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose,busy]);

  return <div className="wv2-drawer-backdrop" role="presentation" onMouseDown={(event) => {
    if (event.target === event.currentTarget && !busy) onClose();
  }}>
    <aside className={`wv2-drawer${wide ? " wv2-drawer--wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
      <header><div><p className="wv2-eyebrow">{eyebrow}</p><h2>{title}</h2></div><button type="button" onClick={onClose} disabled={busy} aria-label="Fermer"><X size={21} /></button></header>
      <div className="wv2-drawer__body">{children}</div>
      {action && actionLabel ? <footer><button type="button" className="wv2-primary" onClick={action}>{actionLabel}<ChevronRight size={18} /></button></footer> : null}
    </aside>
  </div>;
};

const Field = ({ label, children, hint }) => <label className="wv2-field"><span>{label}</span>{children}{hint ? <small>{hint}</small> : null}</label>;

const ConfigurationResourceDrawer = ({ type, record, onClose, onDone }) => {
  const isWarehouse = type === "warehouse";
  const requestIdRef = useRef(makeRequestId(`waise-${type}`));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(isWarehouse ? {
    name: record?.name || "",
    location: record?.location || "",
    description: record?.description || "",
    type: record?.type || "secondary",
  } : {
    name: record?.name || "",
    contact_person: record?.contact_person || "",
    phone: record?.phone || "",
    email: record?.email || "",
    payment_terms: record?.payment_terms || "",
    address: record?.address || "",
  });
  const update = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const save = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return setError(`Le nom ${isWarehouse ? "du lieu" : "du fournisseur"} est obligatoire.`);
    setBusy(true);
    setError("");
    try {
      await mutateWaise(`/api/waise/${isWarehouse ? "warehouses" : "suppliers"}${record?.id ? `/${record.id}` : ""}`, {
        source:record,
        method: record?.id ? "PUT" : "POST",
        body: { ...form, is_active: 1, ...(!record?.id ? { client_request_id: requestIdRef.current } : {}) },
      });
      showWaiseToast("success", isWarehouse ? "Lieu de stockage enregistré." : "Fournisseur enregistré.");
      onDone();
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!record?.id || record?.is_main) return;
    if (!window.confirm(`Supprimer ${record.name} ? Les opérations historiques resteront conservées.`)) return;
    setBusy(true);
    setError("");
    try {
      await mutateWaise(`/api/waise/${isWarehouse ? "warehouses" : "suppliers"}/${record.id}`, { method: "DELETE",source:record });
      showWaiseToast("success", isWarehouse ? "Lieu supprimé de Waise." : "Fournisseur supprimé de Waise.");
      onDone();
    } catch (removeError) {
      setError(removeError.message);
    } finally {
      setBusy(false);
    }
  };
  return <WaiseDrawer title={record?.name || (isWarehouse ? "Nouveau lieu" : "Nouveau fournisseur")} eyebrow="Configuration" onClose={onClose} busy={busy}>
    {error ? <div className="wv2-form-error"><AlertTriangle size={17} />{error}</div> : null}
    <form className="wv2-form" onSubmit={save}>
      <Field label="Nom"><input value={form.name} onChange={update("name")} /></Field>
      {isWarehouse ? <>
        <Field label="Emplacement"><input value={form.location} onChange={update("location")} placeholder="Ex. réserve derrière le bar" /></Field>
        <Field label="Description"><textarea rows="3" value={form.description} onChange={update("description")} /></Field>
        {record?.is_main ? <div className="wv2-drawer-note">Entrepôt Principal est le lieu natif et ne peut pas être supprimé.</div> : null}
      </> : <>
        <Field label="Personne à contacter"><input value={form.contact_person} onChange={update("contact_person")} /></Field>
        <div className="wv2-form-grid"><Field label="Téléphone"><input value={form.phone} onChange={update("phone")} /></Field><Field label="E-mail"><input type="email" value={form.email} onChange={update("email")} /></Field></div>
        <Field label="Conditions de paiement"><input value={form.payment_terms} onChange={update("payment_terms")} placeholder="Ex. comptant, 15 jours" /></Field>
        <Field label="Adresse"><textarea rows="2" value={form.address} onChange={update("address")} /></Field>
      </>}
      <footer>{record?.id && !record?.is_main ? <button type="button" className="wv2-secondary wv2-danger-action" onClick={remove} disabled={busy || !commandAvailable(isWarehouse ? "warehouse.delete" : "supplier.delete",true)}>Supprimer</button> : <span />}<button type="submit" className="wv2-primary" disabled={busy || !commandAvailable(`${isWarehouse ? "warehouse" : "supplier"}.${record?.id ? "update" : "create"}`,Boolean(record?.id))}>{busy ? <Loader2 size={17} className="is-spinning" /> : <Save size={17} />} Enregistrer</button></footer>
    </form>
  </WaiseDrawer>;
};

const LossConfigDrawer = ({ type, record, onClose, onDone }) => {
  const isType = type === "loss_type";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: record?.name || "",
    description: record?.description || "",
    color: record?.color || "#FFB800",
    category: record?.category || "operation",
  });
  const update = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const save = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return setError("Le nom est obligatoire.");
    setBusy(true);
    setError("");
    try {
      await mutateWaise(`/api/waise/stock-settings${record?.id ? `/${record.id}` : ""}`, {
        source:record,
        method: record?.id ? "PUT" : "POST",
        body: { type, data: form },
      });
      showWaiseToast("success", isType ? "Type de perte enregistré." : "Cause de perte enregistrée.");
      onDone();
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!record?.id || !window.confirm(`Supprimer « ${record.name} » ? Les pertes historiques resteront intactes.`)) return;
    setBusy(true);
    setError("");
    try {
      await mutateWaise(`/api/waise/stock-settings/${type}/${record.id}`, { method: "DELETE",source:record });
      showWaiseToast("success", "Réglage supprimé de Waise.");
      onDone();
    } catch (removeError) {
      setError(removeError.message);
    } finally {
      setBusy(false);
    }
  };
  return <WaiseDrawer title={record?.name || (isType ? "Nouveau type de perte" : "Nouvelle cause de perte")} eyebrow="Configuration" onClose={onClose} busy={busy}>
    {error ? <div className="wv2-form-error"><AlertTriangle size={17} />{error}</div> : null}
    <form className="wv2-form" onSubmit={save}>
      <Field label="Nom"><input value={form.name} onChange={update("name")} /></Field>
      <Field label="Description"><textarea rows="3" value={form.description} onChange={update("description")} /></Field>
      {isType ? <Field label="Couleur repère"><input type="color" value={form.color} onChange={update("color")} /></Field> : <Field label="Famille"><input value={form.category} onChange={update("category")} placeholder="Ex. production, service, casse" /></Field>}
      <footer>{record?.id ? <button type="button" className="wv2-secondary wv2-danger-action" onClick={remove} disabled={busy || !commandAvailable("stock-setting.delete",true)}>Supprimer</button> : <span />}<button type="submit" className="wv2-primary" disabled={busy || !commandAvailable(record?.id ? "stock-setting.update" : "stock-setting.create",Boolean(record?.id))}>{busy ? <Loader2 size={17} className="is-spinning" /> : <Save size={17} />} Enregistrer</button></footer>
    </form>
  </WaiseDrawer>;
};

const WaiseActionDrawer = ({ type, seedItemId, onClose, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lookups, setLookups] = useState({ warehouses: [], suppliers: [], items: [], products: [], lossTypes: [], lossCauses: [] });
  const [form, setForm] = useState({ quantity: "", unit_cost: "", counted_quantity: "", produced_quantity: "1", notes: "" });

  useEffect(() => {
    let active = true;
    Promise.all([
      fetchWaise("/api/waise/warehouses"),
      fetchWaise("/api/waise/suppliers"),
      fetchWaise("/api/waise/stock-items"),
      fetchWaise("/api/products"),
      fetchWaise("/api/waise/loss-types"),
      fetchWaise("/api/waise/loss-causes"),
    ]).then(([warehouses, suppliers, items, products, lossTypes, lossCauses]) => {
      if (!active) return;
      setLookups({ warehouses, suppliers, items, products, lossTypes, lossCauses });
      const defaultWarehouse = getDefaultWarehouse(warehouses);
      const seededItem = items.find((entry) => Number(entry.id) === Number(seedItemId));
      const preferredWarehouseId = seededItem?.warehouse_id || defaultWarehouse?.id;
      setForm((current) => ({
        ...current,
        warehouse_id: current.warehouse_id || (preferredWarehouseId ? String(preferredWarehouseId) : ""),
        stock_item_id: current.stock_item_id || (seededItem?.id ? String(seededItem.id) : ""),
      }));
      setLoading(false);
    }).catch((lookupError) => {
      if (!active) return;
      setError(lookupError.message);
      setLoading(false);
    });
    return () => { active = false; };
  }, [seedItemId]);

  const update = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const selectedItem = lookups.items.find((item) => String(item.id) === String(form.stock_item_id));
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      let result = null;
      if (type === "loss") {
        if (!form.warehouse_id || !form.stock_item_id || Number(form.quantity) <= 0) throw new Error("Choisissez le lieu, le produit et une quantité positive.");
        result = await mutateWaise("/api/waise/loss-reports", { body: {
          stock_item_id: Number(form.stock_item_id), warehouse_id: Number(form.warehouse_id), quantity: Number(form.quantity),
          loss_type: lookups.lossTypes.find((entry) => String(entry.id) === String(form.loss_type_id))?.name || "Perte",
          loss_cause: lookups.lossCauses.find((entry) => String(entry.id) === String(form.loss_cause_id))?.name || null,
          loss_value: Number(form.quantity) * Number(selectedItem?.weighted_avg_cost || selectedItem?.cost_price || 0), notes: form.notes || null,
          client_request_id: makeRequestId("waise-loss"),
        } });
      } else if (type === "inventory") {
        if (!form.warehouse_id) throw new Error("Choisissez le lieu à compter.");
        result = await mutateWaise("/api/waise/inventories", { body: { warehouse_id: Number(form.warehouse_id), notes: form.notes || null, client_request_id: makeRequestId("waise-inventory") } });
      } else if (type === "recipe") {
        if (!form.product_id || !form.stock_item_id || Number(form.quantity) <= 0) throw new Error("Choisissez le plat, son ingrédient et une quantité positive.");
        const product = lookups.products.find((entry) => String(entry.id) === String(form.product_id));
        result = await mutateWaise("/api/waise/recipes", { body: {
          product_id: Number(form.product_id), name: form.name || `Recette ${product?.name || "produit"}`, produced_quantity: Number(form.produced_quantity) || 1,
          items: [{ stock_item_id: Number(form.stock_item_id), quantity: Number(form.quantity), yield_percent: 100 }],
          client_request_id: makeRequestId("waise-recipe"),
        } });
      } else if (['movement','batch'].includes(type)) {
        if(!form.warehouse_id || !form.stock_item_id || !(Number(form.quantity)>0)) throw new Error('Choisissez le lieu, le produit et une quantité positive.');
        if(type==='batch' && (!form.batch_number?.trim() || !form.expiry_date)) throw new Error('Indiquez le numéro du lot et sa date de péremption.');
        if(type==='movement' && !form.notes?.trim()) throw new Error('Indiquez le motif du mouvement.');
        const cost=form.unit_cost==='' ? null : Number(form.unit_cost);
        if(cost!=null && (!Number.isFinite(cost) || cost<0)) throw new Error("Prix d'achat invalide.");
        result=await mutateWaise(type==='batch' ? '/api/waise/perishable-batches' : '/api/waise/stock-movements',{body:{
          stock_item_id:Number(form.stock_item_id),warehouse_id:Number(form.warehouse_id),quantity:Number(form.quantity),
          ...(type==='batch' ? {batch_number:form.batch_number.trim(),purchase_date:form.purchase_date || new Date().toISOString().slice(0,10),expiry_date:form.expiry_date,cost_per_unit:cost}
            : {movement_type:form.movement_type || 'in',unit_cost:cost,reason:form.notes.trim()}),
          notes:form.notes || null,client_request_id:makeRequestId(`waise-${type}`),
        }});
        showWaiseToast('success',type==='batch' ? 'Lot réceptionné.' : 'Mouvement enregistré.');
      }
      if (type === "loss") showWaiseToast("success", "Perte soumise au manager. Le stock ne change pas encore.");
      if (type === "inventory") showWaiseToast("success", "Inventaire démarré.");
      onDone(result);
    } catch (submitError) {
      if (type === "inventory" && submitError.code === "WAISE_INVENTORY_ALREADY_OPEN" && Number(submitError.details?.inventory_id) > 0) {
        showWaiseToast("info", "Un inventaire est déjà ouvert pour ce lieu. Continuez ce comptage ou annulez-le.");
        onDone({ id: Number(submitError.details.inventory_id) });
        return;
      }
      setError(submitError.message);
    } finally {
      setBusy(false);
    }
  };

  const titles = { loss: "Déclarer une perte", inventory: "Démarrer un inventaire", recipe: "Créer une recette",movement:'Mouvement de stock',batch:'Réceptionner un lot périssable' };
  return <WaiseDrawer title={titles[type]} eyebrow="Action guidée" onClose={onClose} busy={busy}>
    {error ? <div className="wv2-form-error"><AlertTriangle size={17} />{error}</div> : null}
    <p className="wv2-drawer-intro">L'action est enregistrée avec votre identité. Un double clic ne la duplique pas.</p>
    {loading ? <div className="wv2-loading"><Loader2 className="is-spinning" /><span>Préparation du formulaire…</span></div> : <form className="wv2-form wv2-workflow-form" onSubmit={submit}>
      {(["loss", "inventory","movement","batch"].includes(type)) ? <Field label="Lieu de stock"><select value={form.warehouse_id || ""} onChange={update("warehouse_id")}>{lookups.warehouses.filter((entry) => entry.is_active !== 0).map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}</select></Field> : null}
      {type === "recipe" ? <><Field label="Plat vendu"><select value={form.product_id || ""} onChange={update("product_id")}><option value="">Choisir…</option>{lookups.products.filter((entry) => entry.is_available !== 0).map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}</select></Field><Field label="Nom de la recette"><input value={form.name || ""} onChange={update("name")} placeholder="Ex. Sauce graine — 1 portion" /></Field></> : null}
      {type !== "inventory" ? <Field label={type === "recipe" ? "Ingrédient ou consommable" : "Produit de stock"}><select value={form.stock_item_id || ""} onChange={update("stock_item_id")}><option value="">Choisir…</option>{lookups.items.filter((entry) => entry.is_active !== 0).map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}</select></Field> : null}
      {type==='movement' ? <Field label="Mouvement"><select value={form.movement_type || 'in'} onChange={update('movement_type')}><option value="in">Entrée</option><option value="out">Sortie</option></select></Field> : null}
      {['movement','batch'].includes(type) ? <Field label="Prix d'achat unitaire (facultatif)"><input type="number" min="0" step="0.0001" value={form.unit_cost} onChange={update('unit_cost')}/></Field> : null}
      {type==='batch' ? <><Field label="Numéro du lot"><input value={form.batch_number || ''} onChange={update('batch_number')}/></Field><Field label="Date d'achat"><input type="date" value={form.purchase_date || ''} onChange={update('purchase_date')}/></Field><Field label="Date de péremption"><input type="date" value={form.expiry_date || ''} onChange={update('expiry_date')}/></Field></> : null}
      {type !== "inventory" ? <Field label="Quantité"><input type="number" min="0" step="0.001" value={form.quantity} onChange={update("quantity")} /></Field> : null}
      {type === "recipe" ? <Field label="Nombre de portions produites"><input type="number" min="0.001" step="0.001" value={form.produced_quantity} onChange={update("produced_quantity")} /></Field> : null}
      {type === "loss" ? <div className="wv2-form-grid"><Field label="Type de perte"><select value={form.loss_type_id || ""} onChange={update("loss_type_id")}><option value="">Perte</option>{lookups.lossTypes.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}</select></Field><Field label="Cause"><select value={form.loss_cause_id || ""} onChange={update("loss_cause_id")}><option value="">Non précisée</option>{lookups.lossCauses.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}</select></Field></div> : null}
      {type !== "recipe" ? <Field label={type==='movement' ? 'Motif' : 'Note (facultatif)'}><textarea rows="3" value={form.notes} onChange={update("notes")} /></Field> : null}
      <footer><button type="button" className="wv2-secondary" onClick={onClose} disabled={busy}>Annuler</button><button type="submit" className="wv2-primary" disabled={busy || !commandAvailable(({loss:"loss.create",inventory:"inventory.create",recipe:"recipe.create",movement:"stock-movement.create",batch:"perishable-batch.create"})[type])}>{busy ? <Loader2 size={17} className="is-spinning" /> : <Save size={17} />} Enregistrer</button></footer>
    </form>}
  </WaiseDrawer>;
};

const RecipeDrawer = ({ seedRecipe = null, onClose, onDone }) => {
  const [loading, setLoading] = useState(true);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [products, setProducts] = useState([]);
  const [extras, setExtras] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [targetKey, setTargetKey] = useState("");
  const [recipeId, setRecipeId] = useState(null);
  const [recipeSource,setRecipeSource]=useState(null);
  const [componentId, setComponentId] = useState("");
  const [form, setForm] = useState({ name: "", sku: "", produced_quantity: "1", items: [] });

  const targets = useMemo(() => [...products.flatMap((product) => {
    if (product?.is_active === 0 || product?.is_available === 0) return [];
    const variations = Array.isArray(product?.variations)
      ? product.variations.filter((variation) => variation?.is_active !== 0)
      : [];
    const base = {
      key: `product:${product.id}:0`,
      type: "product",
      productId: Number(product.id),
      variationId: null,
      label: variations.length ? `${product.name} — recette générale` : product.name,
      defaultName: product.name,
    };
    if (!variations.length) return [base];
    return [
      base,
      ...variations.map((variation) => ({
        key: `product:${product.id}:${variation.id}`,
        type: "product",
        productId: Number(product.id),
        variationId: Number(variation.id),
        label: `${product.name} — ${variation.name || "Déclinaison"}`,
        defaultName: `${product.name} — ${variation.name || "Déclinaison"}`,
      })),
    ];
  }), ...extras.filter((extra) => extra?.is_active !== 0).map((extra) => ({
    key: `extra:${extra.id}`,
    type: "extra",
    extraId: Number(extra.id),
    productId: null,
    variationId: null,
    label: extra.name,
    defaultName: extra.name,
  }))], [extras, products]);
  const selectedTarget = targets.find((entry) => entry.key === targetKey) || null;
  const availableComponents = useMemo(() => stockItems
    .filter((item) => item?.is_active !== 0 && String(item?.inventory_kind || "").toLowerCase() !== "composed")
    .sort((left, right) => {
      const typeOrder = { "Ingrédient": 0, "Consommable": 1, "Produit revendu": 2 };
      return (typeOrder[stockNatureLabel(left)] ?? 9) - (typeOrder[stockNatureLabel(right)] ?? 9)
        || String(left.name || "").localeCompare(String(right.name || ""), "fr");
    }), [stockItems]);

  useEffect(() => {
    let active = true;
    Promise.all([fetchWaise("/api/products"), fetchWaise("/api/waise/stock-items"), fetchWaise("/api/waise/extras/stock-tracking")])
      .then(([nextProducts, nextStockItems, nextExtras]) => {
        if (!active) return;
        setProducts(Array.isArray(nextProducts) ? nextProducts : []);
        setStockItems(Array.isArray(nextStockItems) ? nextStockItems : []);
        setExtras(Array.isArray(nextExtras) ? nextExtras : []);
        setLoading(false);
      })
      .catch((loadError) => {
        if (!active) return;
        setError(loadError.message);
        setLoading(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if ((!seedRecipe?.product_id && !seedRecipe?.extra_id) || !targets.length || targetKey) return;
    const key = seedRecipe?.extra_id
      ? `extra:${seedRecipe.extra_id}`
      : `product:${seedRecipe.product_id}:${seedRecipe.variation_id || 0}`;
    if (targets.some((target) => target.key === key)) setTargetKey(key);
  }, [seedRecipe, targetKey, targets]);

  useEffect(() => {
    if (!selectedTarget) {
      setRecipeId(null);
      setForm({ name: "", sku: "", produced_quantity: "1", items: [] });
      return undefined;
    }
    let active = true;
    setRecipeLoading(true);
    setError("");
    const params = new URLSearchParams();
    if (selectedTarget.type === "extra") params.set("extra_id", String(selectedTarget.extraId));
    else {
      params.set("product_id", String(selectedTarget.productId));
      if (selectedTarget.variationId) params.set("variation_id", String(selectedTarget.variationId));
    }
    fetchWaise(`/api/waise/recipes?${params.toString()}`)
      .then((recipes) => {
        if (!active) return;
        const existing = Array.isArray(recipes) ? recipes[0] : null;
        setRecipeId(existing?.id || null);
        setRecipeSource(existing);
        setForm(existing ? {
          name: existing.name || selectedTarget.defaultName,
          sku: existing.sku || "",
          produced_quantity: String(existing.produced_quantity || 1),
          items: (existing.items || []).map((item) => ({
            line_id: makeRequestId("recipe-line"),
            stock_item_id: Number(item.stock_item_id),
            name: item.stock_name || "Article",
            nature: stockNatureLabel({ type: item.stock_type }),
            unit: item.unit_symbol || "unité",
            unit_id: item.unit_id || null,
            unit_cost: Number(item.weighted_avg_cost || item.cost_price || 0),
            quantity: String(item.quantity || 1),
            yield_percent: String(item.yield_percent || 100),
          })),
        } : { name: selectedTarget.defaultName, sku: "", produced_quantity: "1", items: [] });
      })
      .catch((loadError) => active && setError(loadError.message))
      .finally(() => active && setRecipeLoading(false));
    return () => { active = false; };
  }, [selectedTarget]);

  const addComponent = () => {
    const source = availableComponents.find((item) => String(item.id) === String(componentId));
    if (!source) return;
    if (form.items.some((item) => Number(item.stock_item_id) === Number(source.id))) {
      showWaiseToast("info", "Cet article est déjà présent dans la recette.");
      return;
    }
    setForm((current) => ({ ...current, items: [...current.items, {
      line_id: makeRequestId("recipe-line"),
      stock_item_id: Number(source.id),
      name: source.name || "Article",
      nature: stockNatureLabel(source),
      unit: source.unit_symbol || source.unit_name || "unité",
      unit_id: source.unit_id || null,
      unit_cost: Number(source.weighted_avg_cost || source.cost_price || 0),
      quantity: "1",
      yield_percent: "100",
    }] }));
    setComponentId("");
  };
  const updateLine = (lineId, field, value) => setForm((current) => ({
    ...current,
    items: current.items.map((item) => item.line_id === lineId ? { ...item, [field]: value } : item),
  }));
  const removeLine = (lineId) => setForm((current) => ({
    ...current,
    items: current.items.filter((item) => item.line_id !== lineId),
  }));
  const totals = useMemo(() => {
    const total = form.items.reduce((sum, item) => {
      const quantity = Math.max(Number(item.quantity) || 0, 0);
      const yieldPercent = Math.max(Number(item.yield_percent) || 100, 0.001);
      return sum + (quantity / (yieldPercent / 100)) * Number(item.unit_cost || 0);
    }, 0);
    return { total, perUnit: total / Math.max(Number(form.produced_quantity) || 1, 0.001) };
  }, [form.items, form.produced_quantity]);

  const submit = async (event) => {
    event.preventDefault();
    if (!selectedTarget) return setError("Choisissez le plat ou la déclinaison à configurer.");
    if (!form.items.length) return setError("Ajoutez au moins un ingrédient ou consommable.");
    if (form.items.some((item) => Number(item.quantity) <= 0)) return setError("Chaque quantité doit être positive.");
    setBusy(true);
    setError("");
    try {
      const payload = {
        product_id: selectedTarget.productId,
        extra_id: selectedTarget.extraId || null,
        variation_id: selectedTarget.variationId,
        name: form.name.trim() || selectedTarget.defaultName,
        sku: form.sku.trim() || null,
        produced_quantity: Number(form.produced_quantity) || 1,
        items: form.items.map((item) => ({
          stock_item_id: Number(item.stock_item_id),
          quantity: Number(item.quantity),
          yield_percent: Number(item.yield_percent) || 100,
          unit_id: item.unit_id || null,
        })),
        client_request_id: makeRequestId("waise-recipe"),
      };
      const saved = await mutateWaise(recipeId ? `/api/waise/recipes/${recipeId}` : "/api/waise/recipes", {
        source:recipeSource,
        method: recipeId ? "PUT" : "POST",
        body: payload,
      });
      showWaiseToast("success", recipeId ? "Recette mise à jour." : "Recette enregistrée.");
      onDone(saved);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!recipeId || busy || !window.confirm("Supprimer cette recette ? Son historique restera conservé.")) return;
    setBusy(true);
    setError("");
    try {
      await mutateWaise(`/api/waise/recipes/${recipeId}`, { method: "DELETE",source:recipeSource });
      showWaiseToast("success", "Recette supprimée de Waise. Son historique est conservé.");
      onDone({ id: recipeId, deleted: true });
    } catch (removeError) {
      setError(removeError.message);
    } finally {
      setBusy(false);
    }
  };

  return <WaiseDrawer title="Configurer une recette" eyebrow="Produit, déclinaison et composants" onClose={onClose} busy={busy} wide>
    {error ? <div className="wv2-form-error"><AlertTriangle size={17} />{error}</div> : null}
    {loading ? <div className="wv2-loading"><Loader2 className="is-spinning" /><span>Chargement des produits et du stock…</span></div> : <form className="wv2-form wv2-recipe-builder" onSubmit={submit}>
      <Field label="Produit ou déclinaison" hint="Chaque élément vendu peut avoir sa propre recette et ses propres consommations."><select value={targetKey} onChange={(event) => setTargetKey(event.target.value)}><option value="">Choisir…</option>{targets.map((target) => <option key={target.key} value={target.key}>{target.label}</option>)}</select></Field>
      {recipeLoading ? <div className="wv2-loading"><Loader2 className="is-spinning" /><span>Recherche de la recette existante…</span></div> : selectedTarget ? <>
        <div className="wv2-form-grid"><Field label="Nom de la recette"><input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} /></Field><Field label="Quantité produite"><input type="number" min="0.001" step="0.001" value={form.produced_quantity} onChange={(event) => setForm((current) => ({ ...current, produced_quantity: event.target.value }))} /></Field></div>
        <section className="wv2-recipe-components"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Composition</p><h3>Ingrédients, consommables et revendus</h3></div></div>
          <div className="wv2-recipe-add"><select value={componentId} onChange={(event) => setComponentId(event.target.value)}><option value="">Ajouter un composant…</option>{availableComponents.map((item) => <option value={item.id} key={item.id}>{stockNatureLabel(item)} · {item.name}{stockIdentitySuffix(item)}</option>)}</select><button type="button" className="wv2-secondary" onClick={addComponent} disabled={!componentId}><Plus size={17} /> Ajouter</button></div>
          <div className="wv2-recipe-lines">{form.items.map((item) => <div className="wv2-recipe-line" key={item.line_id}><div><strong>{item.name}</strong><small>{item.nature} · {item.unit}</small></div><Field label="Quantité"><input type="number" min="0.001" step="0.001" value={item.quantity} onChange={(event) => updateLine(item.line_id, "quantity", event.target.value)} /></Field><Field label="Rendement %"><input type="number" min="0.01" max="100" step="0.01" value={item.yield_percent} onChange={(event) => updateLine(item.line_id, "yield_percent", event.target.value)} /></Field><button type="button" onClick={() => removeLine(item.line_id)} aria-label={`Retirer ${item.name}`}><Trash2 size={17} /></button></div>)}</div>
          {!form.items.length ? <Empty title="Recette encore vide" note="Ajoutez autant d'ingrédients et consommables que nécessaire." /> : null}
        </section>
        <div className="wv2-recipe-total"><span>Coût connu de la recette <small>Les prix manquants restent signalés dans Stock.</small></span><strong>{Number(form.produced_quantity || 1) > 1 ? `${formatMoney(totals.total)} pour ${Number(form.produced_quantity)} portions · ${formatMoney(totals.perUnit)} / portion` : `${formatMoney(totals.perUnit)} / portion`}</strong></div>
        <footer>{recipeId ? <button type="button" className="wv2-secondary wv2-danger-action" onClick={remove} disabled={busy || !commandAvailable("recipe.delete",true)}>Supprimer</button> : <button type="button" className="wv2-secondary" onClick={onClose} disabled={busy}>Annuler</button>}<button type="submit" className="wv2-primary" disabled={busy || !commandAvailable(recipeId ? "recipe.update" : "recipe.create",Boolean(recipeId))}>{busy ? <Loader2 size={17} className="is-spinning" /> : <Save size={17} />} {recipeId ? "Mettre à jour" : "Enregistrer"}</button></footer>
      </> : null}
    </form>}
  </WaiseDrawer>;
};

const buildPurchaseLine = (item) => {
  const packaging = Array.isArray(item?.packaging) ? item.packaging : [];
  const defaultPackaging = packaging.find((entry) => Number(entry.is_default) === 1) || packaging[0] || null;
  const packagingQuantity = Math.max(Number(defaultPackaging?.packaging_qty) || 1, 1);
  const baseCost = Number(defaultPackaging?.purchase_price)
    || Number(item?.purchase_price_per_unit)
    || (Number(item?.weighted_avg_cost || item?.cost_price) || 0) * packagingQuantity;
  return {
    stock_item_id: Number(item.id),
    name: item.name || "Produit",
    category: item.category || "Sans catégorie",
    nature: stockNatureLabel(item),
    variation: stockIdentityLabel(item),
    quantity: String(Math.max(Number(item.reorder_quantity) || 1, 1)),
    unit_cost: String(Math.max(baseCost, 0)),
    unit: defaultPackaging?.packaging_unit || item.unit_symbol || item.unit_name || "unité",
    packaging_id: defaultPackaging?.id || null,
    packaging_unit: defaultPackaging?.packaging_unit || null,
    packaging_qty: packagingQuantity,
    packaging_options: packaging,
  };
};

const PurchaseOrderDrawer = ({ seedItemId, onClose, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [lookups, setLookups] = useState({ warehouses: [], suppliers: [], items: [] });
  const [form, setForm] = useState({ supplier_id: "", warehouse_id: "", expected_date: "", notes: "", delivery_fee: "" });
  const [lines, setLines] = useState([]);
  const [createdOrder, setCreatedOrder] = useState(null);
  const requestIdRef = useRef(makeRequestId("waise-po"));

  useEffect(() => {
    let active = true;
    Promise.all([
      fetchWaise("/api/waise/warehouses"),
      fetchWaise("/api/waise/suppliers"),
      fetchWaise("/api/waise/stock-items"),
    ]).then(([warehouses, suppliers, items]) => {
      if (!active) return;
      const defaultWarehouse = getDefaultWarehouse(warehouses);
      const seededItem = items.find((entry) => Number(entry.id) === Number(seedItemId));
      setLookups({ warehouses, suppliers, items });
      setForm((current) => ({
        ...current,
        warehouse_id: defaultWarehouse?.id ? String(defaultWarehouse.id) : "",
        supplier_id: seededItem?.supplier_id ? String(seededItem.supplier_id) : current.supplier_id,
      }));
      if (seededItem) setLines([buildPurchaseLine(seededItem)]);
      setLoading(false);
    }).catch((loadError) => {
      if (!active) return;
      setError(loadError.message);
      setLoading(false);
    });
    return () => { active = false; };
  }, [seedItemId]);

  const activeWarehouses = lookups.warehouses.filter((entry) => entry.is_active !== 0);
  const supplierId = Number(form.supplier_id) || null;
  const normalizedQuery = query.trim().toLocaleLowerCase("fr");
  const catalog = lookups.items.filter((item) => {
    if (item.is_active === 0 || lines.some((line) => line.stock_item_id === Number(item.id))) return false;
    const supplierIds = [Number(item.supplier_id), ...(Array.isArray(item.supplier_ids) ? item.supplier_ids.map(Number) : [])].filter(Boolean);
    if (supplierId && supplierIds.length && !supplierIds.includes(supplierId)) return false;
    if (!normalizedQuery) return true;
    return [item.name, item.sku, item.category].filter(Boolean).join(" ").toLocaleLowerCase("fr").includes(normalizedQuery);
  });
  const subtotal = lines.reduce((sum, line) => sum + (Number(line.quantity) || 0) * (Number(line.unit_cost) || 0), 0);
  const total = subtotal + Math.max(Number(form.delivery_fee) || 0, 0);

  const downloadCreatedOrder = async () => {
    if (!createdOrder?.id) return;
    setBusy(true);
    setError("");
    try {
      let readyOrder = createdOrder;
      if (!readyOrder.pdfReady && isRemoteCloudHost(window.location?.hostname)) {
        const projected = await waitForPurchaseOrderProjection(readyOrder.id);
        readyOrder = {
          ...readyOrder,
          number: projected?.order_number || readyOrder.number,
          pdfReady: true,
        };
        setCreatedOrder(readyOrder);
      }
      await downloadPurchaseOrderPdf(readyOrder);
    } catch (downloadError) {
      setError(downloadError.message);
    } finally {
      setBusy(false);
    }
  };

  const updateLine = (id, key, value) => setLines((current) => current.map((line) => {
    if (line.stock_item_id !== id) return line;
    if (key !== "packaging_id") return { ...line, [key]: value };
    const selectedPackaging = line.packaging_options.find((entry) => Number(entry.id) === Number(value));
    if (!selectedPackaging) return line;
    const packagingQty = Math.max(Number(selectedPackaging.packaging_qty) || 1, 1);
    const item = lookups.items.find((entry) => Number(entry.id) === id);
    const price = Number(selectedPackaging.purchase_price)
      || (Number(item?.weighted_avg_cost || item?.cost_price) || 0) * packagingQty;
    return { ...line, packaging_id: Number(value), packaging_unit: selectedPackaging.packaging_unit || null, packaging_qty: packagingQty, unit: selectedPackaging.packaging_unit || line.unit, unit_cost: String(Math.max(price, 0)) };
  }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.supplier_id) return setError("Choisissez le fournisseur de ce bon.");
    if (!form.warehouse_id) return setError("Choisissez le lieu qui recevra la livraison.");
    if (!lines.length) return setError("Ajoutez au moins un article au bon.");
    if (lines.some((line) => !(Number(line.quantity) > 0) || Number(line.unit_cost) < 0)) return setError("Chaque article doit avoir une quantité positive et un prix valide.");
    setBusy(true);
    setError("");
    try {
      const result = await mutateWaise("/api/waise/purchase-orders", { awaitCloudResult: true, body: {
        supplier_id: Number(form.supplier_id),
        warehouse_id: Number(form.warehouse_id),
        status: "pending",
        expected_delivery_date: form.expected_date ? `${form.expected_date}T12:00:00` : null,
        notes: form.notes || null,
        delivery_fee: Math.max(Number(form.delivery_fee) || 0, 0),
        items: lines.map((line) => ({
          stock_item_id: line.stock_item_id,
          quantity: Number(line.quantity),
          unit_cost: Number(line.unit_cost) || 0,
          packaging_id: line.packaging_id,
          packaging_unit: line.packaging_unit || line.unit,
          packaging_qty: Math.max(Number(line.packaging_qty) || 1, 1),
        })),
        client_request_id: requestIdRef.current,
      } });
      if (!(Number(result.id) > 0)) {
        throw new Error("Le serveur local a créé le bon sans retourner son identifiant. Actualisez WAISE avant de télécharger le PDF.");
      }
      let saved = {
        id: Number(result.id),
        number: result.order_number,
        pdfReady: !isRemoteCloudHost(window.location?.hostname),
      };
      setCreatedOrder(saved);
      if (isRemoteCloudHost(window.location?.hostname)) {
        try {
          const projected = await waitForPurchaseOrderProjection(saved.id);
          saved = { ...saved, number: projected?.order_number || saved.number, pdfReady: true };
          setCreatedOrder(saved);
        } catch (projectionError) {
          setError(projectionError.message);
        }
      }
      showWaiseToast("success", "Bon d'achat enregistré. Le stock ne changera qu'à la réception.");
      onSaved(saved);
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setBusy(false);
    }
  };

  if (createdOrder) return <WaiseDrawer title={createdOrder.number || "Bon créé"} eyebrow="Bon d'achat enregistré" onClose={onClose}>
    <div className="wv2-purchase-success"><CheckCircle2 size={42} /><h3>Le bon est prêt</h3><p>Il est enregistré sans modifier le stock. Le stock changera uniquement à la réception.</p><button type="button" className="wv2-primary" disabled={busy} onClick={downloadCreatedOrder}>{busy ? <Loader2 size={17} className="is-spinning" /> : <Download size={17} />} {createdOrder.pdfReady ? "Télécharger le PDF" : "Vérifier et télécharger le PDF"}</button>{error ? <div className="wv2-form-error"><AlertTriangle size={17} />{error}</div> : null}</div>
  </WaiseDrawer>;

  return <WaiseDrawer title="Créer un bon d'achat" eyebrow="Commande fournisseur" onClose={onClose} wide busy={busy}>
    {error ? <div className="wv2-form-error"><AlertTriangle size={17} />{error}</div> : null}
    {loading ? <div className="wv2-loading"><Loader2 className="is-spinning" /><span>Préparation du bon…</span></div> : <form className="wv2-form wv2-purchase-form" onSubmit={submit}>
      <div className="wv2-form-grid"><Field label="Fournisseur"><select value={form.supplier_id} onChange={(event) => setForm((current) => ({ ...current, supplier_id: event.target.value }))}><option value="">Choisir…</option>{lookups.suppliers.filter((entry) => entry.is_active !== 0).map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}</select></Field><Field label="Lieu de réception"><select value={form.warehouse_id} onChange={(event) => setForm((current) => ({ ...current, warehouse_id: event.target.value }))}>{activeWarehouses.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}</select></Field></div>
      <div className="wv2-form-grid"><Field label="Livraison prévue"><input type="date" value={form.expected_date} onChange={(event) => setForm((current) => ({ ...current, expected_date: event.target.value }))} /></Field><Field label="Frais de livraison"><input type="number" min="0" step="1" value={form.delivery_fee} onChange={(event) => setForm((current) => ({ ...current, delivery_fee: event.target.value }))} placeholder="0" /></Field></div>
      <section className="wv2-purchase-builder"><div className="wv2-purchase-builder__head"><div><h3>Articles du bon</h3><small>{lines.length} article{lines.length > 1 ? "s" : ""} sélectionné{lines.length > 1 ? "s" : ""}</small></div><div className="wv2-inline-search"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Ajouter un article…" /></div></div>
        <div className="wv2-purchase-catalog">{catalog.slice(0, 12).map((item) => <button type="button" key={item.id} onClick={() => setLines((current) => [...current, buildPurchaseLine(item)])}><Plus size={15} /><span><strong>{item.name}</strong><small>{stockNatureLabel(item)}{stockIdentitySuffix(item)}</small></span></button>)}{!catalog.length ? <small className="wv2-muted">Aucun autre article ne correspond à cette sélection.</small> : null}</div>
        <div className="wv2-purchase-lines">{lines.map((line) => <article key={line.stock_item_id}><div className="wv2-purchase-line__title"><div><strong>{line.name}</strong><small>{line.nature || line.category}{line.variation ? ` · Déclinaison : ${line.variation}` : ""}</small></div><button type="button" aria-label={`Retirer ${line.name}`} onClick={() => setLines((current) => current.filter((entry) => entry.stock_item_id !== line.stock_item_id))}><Minus size={16} /></button></div><div className="wv2-purchase-line__fields"><Field label="Quantité"><input type="number" min="0.001" step="0.001" value={line.quantity} onChange={(event) => updateLine(line.stock_item_id, "quantity", event.target.value)} /></Field>{line.packaging_options.length ? <Field label="Conditionnement"><select value={line.packaging_id || ""} onChange={(event) => updateLine(line.stock_item_id, "packaging_id", event.target.value)}>{line.packaging_options.map((entry) => <option key={entry.id} value={entry.id}>{entry.packaging_unit} × {formatNumber(entry.packaging_qty || 1)}</option>)}</select></Field> : <Field label="Unité"><input value={line.unit} readOnly /></Field>}<Field label="Prix d'achat"><input type="number" min="0" step="1" value={line.unit_cost} onChange={(event) => updateLine(line.stock_item_id, "unit_cost", event.target.value)} /></Field><div className="wv2-purchase-line__total"><span>Total</span><strong>{formatMoney((Number(line.quantity) || 0) * (Number(line.unit_cost) || 0))}</strong></div></div></article>)}</div>
      </section>
      <Field label="Note pour le fournisseur (facultatif)"><textarea rows="3" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} /></Field>
      <div className="wv2-purchase-total"><span><small>Sous-total</small><strong>{formatMoney(subtotal)}</strong></span><span><small>Total estimé</small><strong>{formatMoney(total)}</strong></span></div>
      <footer><button type="button" className="wv2-secondary" onClick={onClose} disabled={busy}>Annuler</button><button type="submit" className="wv2-primary" disabled={busy}>{busy ? <Loader2 size={17} className="is-spinning" /> : <Save size={17} />} Enregistrer le bon</button></footer>
    </form>}
  </WaiseDrawer>;
};

const ReceivePurchaseDialog = ({ order, onClose, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const receive = async () => {
    setBusy(true);
    setError("");
    try {
      await mutateWaise(`/api/waise/purchase-orders/${order.id}/store`, {
        source:order,
        body: {
          mode: "auto",
          warehouse_id: order.warehouse?.id || null,
          client_request_id: makeRequestId(`waise-receipt-${order.id}`),
        },
      });
      showWaiseToast("success", "Livraison réceptionnée. Le stock et les estimations ont été actualisés.");
      onDone();
    } catch (receiveError) {
      setError(receiveError.message);
    } finally {
      setBusy(false);
    }
  };
  return <WaiseDialog title="Réceptionner la livraison" subtitle={`${order.number || `Bon #${order.id}`} · ${order.supplier?.name || "Fournisseur"}`} onClose={onClose} busy={busy} error={error}>
    <div className="wv2-receipt-summary">
      <p>Les quantités commandées ci-dessous seront ajoutées au stock de <strong>{order.warehouse?.name || "l'entrepôt du bon"}</strong>.</p>
      <div className="wv2-list">{(order.lines || []).map((line) => <div className="wv2-row" key={line.id || line.stock_item_id}><div className="wv2-row__main"><strong>{line.name}</strong><small>{formatNumber(line.quantities?.ordered)} {line.unit || "unité(s)"}</small></div></div>)}</div>
      <div className="wv2-dialog-actions"><button type="button" className="wv2-secondary" onClick={onClose} disabled={busy}>Plus tard</button><button type="button" className="wv2-primary" onClick={receive} disabled={busy || !commandAvailable("purchase-order.receive",true)}>{busy ? <Loader2 size={17} className="is-spinning" /> : <ArrowDownToLine size={17} />} Tout réceptionner</button></div>
    </div>
  </WaiseDialog>;
};

const InventoryDialog = ({ inventoryId, focusItemId = null, canFinalize, onClose, onDone }) => {
  const [detail, setDetail] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [counts, setCounts] = useState({});
  const countSources=useRef({});
  const [query, setQuery] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const [error, setError] = useState("");

  const loadInventory = useCallback(async () => {
    const nextDetail = await fetchWaise(`/api/waise/v2/inventories/${inventoryId}`);
    const warehouseId = nextDetail.warehouse_id || nextDetail.warehouse?.id;
    const nextCatalog = warehouseId
      ? await fetchWaise(`/api/waise/inventories/countable-items?warehouse_id=${warehouseId}`)
      : [];
    setDetail(nextDetail);
    setCatalog(nextCatalog);
    setCounts((current) => {
      const next = { ...current };
      (nextDetail.items || []).forEach((item) => {
        const id = item.stock_item_id || item.id;
        if (next[id] === undefined) next[id] = String(item.counted_quantity ?? "");
      });
      return next;
    });
  }, [inventoryId]);

  useEffect(() => {
    loadInventory().catch((loadError) => setError(loadError.message));
  }, [loadInventory]);

  const saveCount = async (item) => {
    const itemId = Number(item.stock_item_id || item.id);
    const quantity = Number(counts[itemId]);
    if (!Number.isFinite(quantity) || quantity < 0) {
      setError("Saisissez une quantité positive ou zéro.");
      return;
    }
    setBusyKey(`item-${itemId}`);
    setError("");
    try {
      await mutateWaise(`/api/waise/inventories/${inventoryId}/items`, { source:countSources.current[itemId] || detail,body: { stock_item_id: itemId, counted_quantity: quantity } });
      delete countSources.current[itemId];
      await loadInventory();
      showWaiseToast("success", `${item.name || item.item_name || "Article"} compté.`);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setBusyKey("");
    }
  };

  const finalize = async () => {
    const reviewSummary = detail?.review_summary;
    const variancePreview = (reviewSummary?.variances || [])
      .slice(0, 3)
      .map((item) => `${item.item_name} : ${Number(item.quantity_difference) > 0 ? "+" : ""}${formatNumber(item.quantity_difference)} ${item.unit_symbol}`)
      .join("\n");
    const confirmation = reviewSummary
      ? `${reviewSummary.counted_items} article(s) compté(s), ${reviewSummary.variance_count} écart(s).${variancePreview ? `\n\n${variancePreview}` : ""}\n\nFinaliser et appliquer ces écarts au stock officiel ?`
      : "Finaliser cet inventaire ?";
    if (!window.confirm(confirmation)) return;
    setBusyKey("finalize");
    setError("");
    try {
      await mutateWaise(`/api/waise/inventories/${inventoryId}/finalize`, { source:detail,body: { client_request_id: makeRequestId(`waise-finalize-${inventoryId}`) } });
      showWaiseToast("success", "Inventaire finalisé. Les écarts et estimations ont été actualisés.");
      onDone();
    } catch (finalizeError) {
      const staleNames = finalizeError.details?.items?.map((item) => item.item_name).filter(Boolean);
      setError(staleNames?.length ? `${finalizeError.message} À recompter : ${staleNames.join(", ")}.` : finalizeError.message);
    } finally {
      setBusyKey("");
    }
  };

  const cancel = async () => {
    if (!["planned", "in_progress"].includes(detail?.status) || !canFinalize) return;
    if (!window.confirm("Annuler cet inventaire ? Les comptages non finalisés seront abandonnés. Le stock ne changera pas.")) return;
    setBusyKey("cancel");
    setError("");
    try {
      const result = await mutateWaise(`/api/waise/inventories/${inventoryId}/cancel`, {
        source:detail,
        body: { client_request_id: makeRequestId(`waise-cancel-inventory-${inventoryId}`) },
      });
      if (!result?.queued) showWaiseToast("success", "Inventaire annulé. Vous pouvez en démarrer un autre pour ce lieu.");
      onDone();
    } catch (cancelError) {
      setError(cancelError.message);
    } finally {
      setBusyKey("");
    }
  };

  const existingById = new Map((detail?.items || []).map((item) => [Number(item.stock_item_id), item]));
  const focusedCatalog = focusItemId
    ? catalog.filter((item) => Number(item.stock_item_id || item.id) === Number(focusItemId))
    : catalog;
  const visible = focusedCatalog.filter((item) => !query || String(item.name || item.item_name || "").toLowerCase().includes(query.toLowerCase()));
  return <WaiseDrawer title={detail?.name || "Comptage en cours"} eyebrow="Inventaire" onClose={onClose} busy={Boolean(busyKey)} wide>
    {error ? <div className="wv2-form-error"><AlertTriangle size={17} />{error}</div> : null}
    <p className="wv2-drawer-intro">{focusItemId ? "Ce comptage ne présente que l'article choisi. Le stock théorique reste masqué." : "Comptez ce que vous voyez. Le stock théorique reste masqué jusqu'à la validation."}</p>
    {!detail ? <div className="wv2-loading"><Loader2 className="is-spinning" /><span>Chargement du comptage…</span></div> : <div className="wv2-inventory-editor">
      {detail.review_summary ? <div className="wv2-inventory-review"><div className="wv2-drawer-status"><ClipboardCheck size={20} /><div><strong>Résumé avant finalisation</strong><p>{detail.review_summary.counted_items} article(s) compté(s) · {detail.review_summary.variance_count} écart(s)</p></div></div>{detail.review_summary.variances?.length ? <div className="wv2-list">{detail.review_summary.variances.map((variance) => <div className="wv2-row" key={variance.stock_item_id}><div className="wv2-row__main"><strong>{variance.item_name}</strong><small>Théorique {formatNumber(variance.expected_quantity)} {variance.unit_symbol} · Compté {formatNumber(variance.counted_quantity)} {variance.unit_symbol}</small></div><div className="wv2-row__amount"><strong>{Number(variance.quantity_difference) > 0 ? "+" : ""}{formatNumber(variance.quantity_difference)} {variance.unit_symbol}</strong><small>{variance.value_status === "known" ? formatMoney(variance.value_difference) : "Valeur inconnue"}</small></div></div>)}</div> : null}</div> : null}
      {!focusItemId ? <input className="wv2-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Chercher un produit à compter…" /> : null}
      <div className="wv2-count-list">{visible.map((item) => {
        const itemId = Number(item.stock_item_id || item.id);
        const existing = existingById.get(itemId);
        return <div className="wv2-count-row" key={itemId}><div><strong>{item.name || item.item_name}</strong><small>{existing ? "Comptage enregistré" : "Pas encore compté"} · {item.unit_symbol || item.unit || "unité"}</small></div><input aria-label={`Quantité ${item.name || item.item_name}`} type="number" min="0" step="0.001" value={counts[itemId] ?? ""} onChange={(event) => { countSources.current[itemId] ||= detail; setCounts((current) => ({ ...current, [itemId]: event.target.value })); }} /><button type="button" onClick={() => saveCount(item)} disabled={Boolean(busyKey) || !commandAvailable("inventory.count",true)}>{busyKey === `item-${itemId}` ? <Loader2 size={16} className="is-spinning" /> : <Save size={16} />}<span>Valider</span></button></div>;
      })}</div>
      <footer className="wv2-dialog-actions"><button type="button" className="wv2-secondary" onClick={onClose} disabled={Boolean(busyKey)}>Fermer</button>{["planned", "in_progress"].includes(detail.status) && canFinalize ? <button type="button" className="wv2-secondary wv2-danger-action" onClick={cancel} disabled={Boolean(busyKey) || !commandAvailable("inventory.cancel",true)}>{busyKey === "cancel" ? <Loader2 size={17} className="is-spinning" /> : <X size={17} />} Annuler l'inventaire</button> : null}{detail.status === "in_progress" && canFinalize ? <button type="button" className="wv2-primary" onClick={finalize} disabled={Boolean(busyKey) || !commandAvailable("inventory.finalize",true)}>{busyKey === "finalize" ? <Loader2 size={17} className="is-spinning" /> : <CheckCircle2 size={17} />} Finaliser l'inventaire</button> : null}</footer>
    </div>}
  </WaiseDrawer>;
};

const TodayView = ({ data, onSelect, onAction, onOpenInventory, onReceivePurchase }) => {
  const [drawer, setDrawer] = useState(null);
  const [showAllAttention, setShowAllAttention] = useState(false);
  const summary = data?.summary || {};
  const by = summary.by_status || {};
  const permissions = data?.permissions || {};
  const attention = data?.attention || [];
  const operations = data?.operations || {};
  const completedToday = data?.completed_today || [];
  const urgent = (by.out_of_stock || 0) + (by.negative || 0);
  const weak = by.low_stock || 0;
  const healthy = by.ok || 0;
  const unknown = by.not_initialized || 0;
  const tracked = summary.tracked_products ?? summary.tracked_items ?? 0;
  const vitalityTotal = Math.max(urgent + weak + healthy + unknown, 1);
  const valuation = summary.valuation;

  const runAttentionAction = (item) => {
    setDrawer(null);
    if (item.available_action === "prepare_purchase_order") onAction({ type: "purchase" });
    else if (item.available_action === "count_stock") onAction("inventory");
    else if (item.available_action === "review_loss") onSelect("losses");
    else if (["configure_recipes", "configure_stock_item"].includes(item.available_action)) onSelect("configuration");
    else onSelect("stock");
  };

  const openReceipts = () => {
    const receivable = (operations.purchase_orders || []).filter((order) => !["completed", "cancelled"].includes(order.status));
    if (!receivable.length) {
      setDrawer({ type: "empty-receipts" });
      return;
    }
    setDrawer({ type: "receipts", orders: receivable });
  };

  const quickActions = [
    { id: "receive", label: "Réceptionner", note: "Une livraison fournisseur", icon: Truck, allowed: permissions.can_receive_stock, action: openReceipts },
    { id: "inventory", label: "Compter le stock", note: "Démarrer un inventaire", icon: ClipboardCheck, allowed: permissions.can_count_stock, action: () => onAction("inventory") },
    { id: "loss", label: "Déclarer une perte", note: "Casse, péremption, erreur", icon: Trash2, allowed: permissions.can_declare_loss, action: () => onAction("loss") },
    { id: "purchase", label: "Créer un bon d'achat", note: "Commander plusieurs articles", icon: ShoppingCart, allowed: permissions.can_create_purchase_order, action: () => onAction({ type: "purchase" }) },
  ].filter((item) => item.allowed);

  return (
    <>
      <section className="wv2-today-hero">
        <div className="wv2-today-hero__pulse">
          <div className={`wv2-pulse__ring ${urgent ? "is-danger" : weak ? "is-warning" : "is-success"}`} aria-hidden="true"><span /></div>
          <div className="wv2-today-hero__verdict">
            <p className="wv2-eyebrow">Le pouls du stock</p>
            <h2>{data?.verdict || "Situation du stock"}</h2>
            <p>{tracked} produit{tracked > 1 ? "s" : ""} suivi{tracked > 1 ? "s" : ""} · dernière lecture locale fiable.</p>
          </div>
          <div className="wv2-vitality" aria-label={`Vitalité du stock : ${urgent} urgences, ${weak} stocks faibles, ${unknown} à initialiser et ${healthy} corrects`}>
            <div className="wv2-vitality__labels"><span><i className="is-danger" />{urgent} rupture{urgent > 1 ? "s" : ""}</span><span><i className="is-warning" />{weak} faible{weak > 1 ? "s" : ""}</span>{unknown ? <span><i className="is-info" />{unknown} à initialiser</span> : null}<span><i className="is-success" />{healthy} correct{healthy > 1 ? "s" : ""}</span></div>
            <div className="wv2-vitality__bar"><i className="is-danger" style={{ width: `${urgent / vitalityTotal * 100}%` }} /><i className="is-warning" style={{ width: `${weak / vitalityTotal * 100}%` }} /><i className="is-info" style={{ width: `${unknown / vitalityTotal * 100}%` }} /><i className="is-success" style={{ width: `${healthy / vitalityTotal * 100}%` }} /></div>
          </div>
        </div>
        <article className="wv2-valuation">
          <div className="wv2-valuation__icon"><ShieldCheck size={23} /></div>
          <div><p className="wv2-eyebrow">Fiabilité des chiffres</p><h3>{valuation ? formatMoney(valuation.known_value) : "Montants masqués"}</h3><p>{valuation ? (valuation.complete ? "Valeur connue du stock" : "Estimation incomplète") : "Vue opérationnelle sans prix d'achat"}</p></div>
          {valuation && !valuation.complete ? <div className="wv2-valuation__issues"><span>{valuation.missing_cost_count || 0} prix manquant{valuation.missing_cost_count > 1 ? "s" : ""}</span><span>{valuation.unreliable_quantity_count || 0} quantité{valuation.unreliable_quantity_count > 1 ? "s" : ""} à vérifier</span></div> : <Badge status="ok">Données cohérentes</Badge>}
        </article>
      </section>

      <section className="wv2-quick-section">
        <div className="wv2-section__head"><div><p className="wv2-eyebrow">Actions rapides</p><h3>Que voulez-vous faire ?</h3></div><small>Une action ne modifie le stock qu'après votre confirmation.</small></div>
        <div className="wv2-quick-actions">{quickActions.map(({ id, label, note, icon: Icon, action }) => <button type="button" className={`wv2-quick-action wv2-quick-action--${id}`} key={id} onClick={action}><span><Icon size={23} /></span><strong>{label}</strong><small>{note}</small><ChevronRight size={18} className="wv2-quick-action__arrow" /></button>)}</div>
      </section>

      <div className="wv2-today-columns">
        <section className="wv2-section wv2-today-attention">
          <div className="wv2-section__head"><div><p className="wv2-eyebrow">À régler maintenant</p><h3>Les priorités du service</h3></div>{attention.length > 5 ? <button type="button" className="wv2-text-button" onClick={() => setShowAllAttention((current) => !current)}>{showAllAttention ? "Réduire" : `Voir les ${attention.length}`}</button> : null}</div>
          <div className="wv2-list">
            {attention.slice(0, showAllAttention ? 30 : 5).map((item, index) => (
              <button key={`${item.kind}-${item.stock_item_id || index}`} className="wv2-row wv2-row--button" onClick={() => setDrawer({ type: "attention", item })}>
                <span className={`wv2-dot wv2-dot--${item.severity}`} />
                <span className="wv2-row__main"><strong>{item.title}</strong><small>{STATUS_LABELS[item.status] || "Configuration à compléter"}{item.threshold !== undefined ? ` · seuil ${formatNumber(item.threshold)} ${item.unit || ""}` : ""}</small></span>
                {item.quantity !== undefined ? <span className="wv2-row__quantity">{formatNumber(item.quantity)} {item.unit || ""}</span> : null}<ChevronRight size={17} />
              </button>
            ))}
            {!attention.length ? <Empty title="Aucune urgence" note="Le stock ne demande pas d'action immédiate." /> : null}
          </div>
        </section>

        <section className="wv2-section wv2-today-operations">
          <div className="wv2-section__head"><div><p className="wv2-eyebrow">En cours aujourd'hui</p><h3>Le travail commencé</h3></div><Clock3 size={20} /></div>
          <div className="wv2-list">
            {(operations.purchase_orders || []).map((order) => <button type="button" className="wv2-operation" key={`purchase-${order.id}`} onClick={() => setDrawer({ type: "purchase", order })}><span className="wv2-operation__icon"><Truck size={19} /></span><span className="wv2-row__main"><strong>{order.supplier?.name || order.number || `Bon #${order.id}`}</strong><small>{STATUS_LABELS[order.status] || order.status} · {order.warehouse?.name || "Lieu non renseigné"}</small><span className="wv2-progress"><i style={{ width: `${order.progress?.percentage || 0}%` }} /></span></span><strong>{order.progress?.percentage || 0}%</strong><ChevronRight size={17} /></button>)}
            {(operations.inventories || []).map((inventory) => <button type="button" className="wv2-operation" key={`inventory-${inventory.id}`} onClick={() => setDrawer({ type: "inventory", inventory })}><span className="wv2-operation__icon"><ClipboardCheck size={19} /></span><span className="wv2-row__main"><strong>{inventory.name || inventory.warehouse?.name || `Inventaire #${inventory.id}`}</strong><small>{inventory.coverage?.counted_items || 0} sur {inventory.coverage?.total_items || inventory.coverage?.current_countable_items || 0} produits comptés</small><span className="wv2-progress"><i style={{ width: `${inventory.coverage?.percentage || 0}%` }} /></span></span><strong>{inventory.coverage?.percentage || 0}%</strong><ChevronRight size={17} /></button>)}
            {!(operations.purchase_orders || []).length && !(operations.inventories || []).length ? <Empty title="Aucune opération en cours" note="Les réceptions et inventaires ouverts apparaîtront ici." /> : null}
          </div>
        </section>
      </div>

      <section className="wv2-section wv2-today-completed">
        <div className="wv2-section__head"><div><p className="wv2-eyebrow">Déjà fait aujourd'hui</p><h3>Les actions enregistrées</h3></div><CheckCircle2 size={21} /></div>
        <div className="wv2-completed-grid">{completedToday.map((item) => <article key={`${item.kind}-${item.id}`}><span><CheckCircle2 size={17} /></span><div><strong>{item.title}</strong><small>{item.meta} · {formatTime(item.occurred_at)}</small></div></article>)}</div>
        {!completedToday.length ? <Empty title="La journée commence" note="Les réceptions, inventaires et pertes validés apparaîtront ici." /> : null}
      </section>

      {drawer?.type === "attention" ? <WaiseDrawer title={drawer.item.title} eyebrow="À régler maintenant" onClose={() => setDrawer(null)} action={drawer.item.available_action ? () => runAttentionAction(drawer.item) : null} actionLabel={drawer.item.available_action === "prepare_purchase_order" ? "Préparer une commande" : drawer.item.available_action === "count_stock" ? "Compter maintenant" : drawer.item.available_action === "review_loss" ? "Examiner la perte" : drawer.item.available_action?.startsWith("configure") ? "Compléter la configuration" : null}>
        <div className="wv2-drawer-status"><AlertTriangle size={22} /><div><Badge status={drawer.item.status}>{STATUS_LABELS[drawer.item.status] || "À vérifier"}</Badge><p>Waise a détecté cette situation dans le stock local. L'ouverture de ce panneau ne modifie aucune donnée.</p></div></div>
        <dl className="wv2-detail-list"><div><dt>Quantité actuelle</dt><dd>{formatNumber(drawer.item.quantity)} {drawer.item.unit || "unité"}</dd></div>{drawer.item.threshold !== undefined ? <div><dt>Seuil prévu</dt><dd>{formatNumber(drawer.item.threshold)} {drawer.item.unit || "unité"}</dd></div> : null}{drawer.item.suggested_order_quantity ? <div><dt>Suggestion Waise</dt><dd>Commander environ {formatNumber(drawer.item.suggested_order_quantity)} {drawer.item.unit || "unité"}</dd></div> : null}</dl>
        {drawer.item.configuration_issues?.length ? <div className="wv2-drawer-note"><AlertTriangle size={18} /><span>{drawer.item.configuration_issues.length} information{drawer.item.configuration_issues.length > 1 ? "s" : ""} de configuration à compléter.</span></div> : null}
      </WaiseDrawer> : null}

      {drawer?.type === "purchase" ? <WaiseDrawer title={drawer.order.number || `Bon #${drawer.order.id}`} eyebrow="Commande fournisseur" onClose={() => setDrawer(null)} action={permissions.can_receive_stock && !["completed", "cancelled"].includes(drawer.order.status) ? () => { setDrawer(null); onReceivePurchase(drawer.order); } : () => { setDrawer(null); onSelect("purchases"); }} actionLabel={permissions.can_receive_stock && !["completed", "cancelled"].includes(drawer.order.status) ? "Réceptionner" : "Voir les achats"}>
        <div className="wv2-drawer-status"><Truck size={22} /><div><Badge status={drawer.order.status} /><p>{drawer.order.supplier?.name || "Fournisseur non renseigné"} · {drawer.order.warehouse?.name || "Lieu non renseigné"}</p></div></div>
        <dl className="wv2-detail-list"><div><dt>Avancement</dt><dd>{drawer.order.progress?.percentage || 0}%</dd></div><div><dt>Lignes</dt><dd>{drawer.order.progress?.line_count || drawer.order.lines?.length || 0}</dd></div>{drawer.order.financials ? <div><dt>Montant</dt><dd>{formatMoney(drawer.order.financials.total_amount)}</dd></div> : null}</dl>
      </WaiseDrawer> : null}

      {drawer?.type === "inventory" ? <WaiseDrawer title={drawer.inventory.name || `Inventaire #${drawer.inventory.id}`} eyebrow="Inventaire en cours" onClose={() => setDrawer(null)} action={() => { setDrawer(null); onOpenInventory(drawer.inventory.id); }} actionLabel="Continuer le comptage">
        <div className="wv2-drawer-status"><ClipboardCheck size={22} /><div><Badge status={drawer.inventory.status} /><p>{drawer.inventory.warehouse?.name || "Lieu non renseigné"}</p></div></div>
        <dl className="wv2-detail-list"><div><dt>Produits comptés</dt><dd>{drawer.inventory.coverage?.counted_items || 0}</dd></div><div><dt>Produits prévus</dt><dd>{drawer.inventory.coverage?.total_items || drawer.inventory.coverage?.current_countable_items || 0}</dd></div><div><dt>Progression</dt><dd>{drawer.inventory.coverage?.percentage || 0}%</dd></div></dl>
      </WaiseDrawer> : null}

      {drawer?.type === "receipts" ? <WaiseDrawer title="Livraisons à réceptionner" eyebrow="Que voulez-vous faire ?" onClose={() => setDrawer(null)}>
        <p className="wv2-drawer-intro">Choisissez une livraison. Rien n'entre en stock avant votre confirmation finale.</p>
        <div className="wv2-list">{drawer.orders.map((order) => <button type="button" className="wv2-row wv2-row--button" key={order.id} onClick={() => { setDrawer(null); onReceivePurchase(order); }}><span className="wv2-operation__icon"><Truck size={18} /></span><span className="wv2-row__main"><strong>{order.supplier?.name || order.number || `Bon #${order.id}`}</strong><small>{STATUS_LABELS[order.status] || order.status} · {order.progress?.remaining || 0} unité(s) restante(s)</small></span><ChevronRight size={17} /></button>)}</div>
      </WaiseDrawer> : null}

      {drawer?.type === "empty-receipts" ? <WaiseDrawer title="Aucune livraison en attente" eyebrow="Réceptionner" onClose={() => setDrawer(null)} action={() => { setDrawer(null); onSelect("purchases"); }} actionLabel="Voir les achats">
        <div className="wv2-drawer-empty"><PackagePlus size={34} /><strong>Tout est à jour</strong><p>Il n'y a aucun bon fournisseur à réceptionner pour le moment.</p></div>
      </WaiseDrawer> : null}
    </>
  );
};

export const isKnownStockItem = (item) =>
  item?.status !== "not_initialized" &&
  item?.quantity !== null &&
  item?.quantity !== undefined &&
  Number.isFinite(Number(item.quantity));

export const isUsableWaiseSectionPayload = (section, payload) => {
  if (!payload || Array.isArray(payload) || typeof payload !== "object") return false;
  if (section === "stock") return Array.isArray(payload.items);
  if (section === "purchases") return Array.isArray(payload.orders);
  if (section === "inventories") return Array.isArray(payload.inventories);
  if (section === "losses") return Array.isArray(payload.losses);
  if (section === "situation") return payload.enabled === false || Array.isArray(payload.rows);
  return true;
};

const STOCK_FILTERS = [
  { id: "all", label: "Tous", test: () => true },
  { id: "known", label: "Stock connu", test: isKnownStockItem },
  { id: "attention", label: "À vérifier", test: (item) => ["negative", "not_initialized"].includes(item.status) },
  { id: "out_of_stock", label: "Ruptures", test: (item) => item.status === "out_of_stock" },
  { id: "low_stock", label: "Stock faible", test: (item) => item.status === "low_stock" },
  { id: "ingredient", label: "Ingrédients", test: (item) => item.type === "ingredient" && item.inventory_kind !== "resold" },
  { id: "resold", label: "Produits revendus", test: (item) => item.type === "resold" || item.inventory_kind === "resold" },
  { id: "consumable", label: "Consommables", test: (item) => ["consumable", "packaging", "cleaning"].includes(item.type) },
];

const STOCK_ISSUE_LABELS = {
  missing_unit: "Unité de stockage manquante",
  missing_warehouse: "Aucun lieu de stock associé",
  missing_purchase_cost: "Prix d'achat manquant",
  missing_supplier: "Fournisseur habituel manquant",
};

const StockView = ({ data, query, setQuery, onAction, onSelect }) => {
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState(null);
  const items = data?.items || [];
  const summary = data?.summary || {};
  const valuation = summary.valuation;
  const permissions = data?.permissions || {};
  const categories = [...new Set(items.map((item) => item.category).filter(Boolean))].sort((left, right) => left.localeCompare(right, "fr"));
  const activeFilter = STOCK_FILTERS.find((entry) => entry.id === filter) || STOCK_FILTERS[0];
  const search = String(query || "").trim().toLocaleLowerCase("fr");
  const visible = items.filter((item) => {
    if (!activeFilter.test(item)) return false;
    if (category !== "all" && item.category !== category) return false;
    if (!search) return true;
    return [item.name, item.category, item.sku, item.supplier?.name]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase("fr")
      .includes(search);
  });

  const quantityLabel = (item) => item.status === "not_initialized"
    ? "Inconnue"
    : `${formatNumber(item.quantity)} ${item.unit || "unité"}`;
  const typeLabel = stockNatureLabel;

  const dateLabel = (value) => {
    if (!value) return "Aucune information enregistrée";
    const date = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(date.getTime())) return "Date non lisible";
    return date.toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  };

  const openWorkflow = (workflow) => {
    setSelected(null);
    onAction({ type: workflow, seedItemId: selected?.id || null });
  };

  return (
    <>
        {permissions.can_receive_stock ? <div className="wv2-actions"><button type="button" className="wv2-secondary" disabled={!commandAvailable('stock-movement.create')} onClick={()=>onAction({type:'movement'})}>Entrée / sortie</button><button type="button" className="wv2-secondary" disabled={!commandAvailable('perishable-batch.create')} onClick={()=>onAction({type:'batch'})}>Lot périssable</button></div> : null}
      <section className="wv2-stock-summary">
        <div><span className="wv2-stock-summary__icon"><Boxes size={20} /></span><strong>{summary.tracked_products ?? summary.tracked_items ?? 0}</strong><small>produits suivis</small></div>
        <div className="is-danger"><span className="wv2-stock-summary__icon"><AlertTriangle size={20} /></span><strong>{(summary.by_status?.out_of_stock || 0) + (summary.by_status?.negative || 0)}</strong><small>ruptures ou négatifs</small></div>
        <div className="is-warning"><span className="wv2-stock-summary__icon"><PackageSearch size={20} /></span><strong>{summary.by_status?.low_stock || 0}</strong><small>stocks faibles</small></div>
        <div><span className="wv2-stock-summary__icon"><ClipboardCheck size={20} /></span><strong>{summary.by_status?.not_initialized || 0}</strong><small>à initialiser</small></div>
        <div className={valuation?.complete ? "is-success" : "is-warning"}><span className="wv2-stock-summary__icon"><ShieldCheck size={20} /></span><strong>{valuation ? formatMoney(valuation.known_value) : "Masquée"}</strong><small>{valuation ? (valuation.complete ? "valeur connue" : `${valuation.missing_cost_count} prix manquants`) : "selon vos droits"}</small></div>
      </section>

      <section className="wv2-stock-controls">
        <div className="wv2-toolbar wv2-stock-search"><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Chercher un produit, une catégorie, une référence…" aria-label="Chercher dans le stock" />{query ? <button type="button" onClick={() => setQuery("")} aria-label="Effacer la recherche"><X size={17} /></button> : null}</div>
        <select className="wv2-stock-category" value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Filtrer par catégorie"><option value="all">Toutes les catégories</option>{categories.map((entry) => <option value={entry} key={entry}>{entry}</option>)}</select>
        <div className="wv2-stock-filters">{STOCK_FILTERS.map((entry) => <button type="button" key={entry.id} className={filter === entry.id ? "is-active" : ""} onClick={() => setFilter(entry.id)}>{entry.label}<span>{items.filter(entry.test).length}</span></button>)}</div>
      </section>

      <section className="wv2-stock-list" aria-label="Produits du stock">
        <div className="wv2-stock-list__head"><span>Produit</span><span>Situation</span><span>Emplacement</span><span>Valeur</span><span /></div>
        {visible.map((item) => <button type="button" className={`wv2-stock-row wv2-stock-row--${statusTone(item.status)}`} key={item.id} onClick={() => setSelected(item)}>
          <span className="wv2-stock-row__product"><i><Boxes size={19} /></i><span><strong>{item.name}</strong><small>{typeLabel(item)}{stockIdentitySuffix(item)}{item.sku ? ` · ${item.sku}` : ""}</small></span></span>
          <span className="wv2-stock-row__state"><Badge status={item.status} /><strong>{quantityLabel(item)}</strong><small>{item.status === "not_initialized" ? "Premier comptage nécessaire" : `Seuil : ${formatNumber(item.threshold)} ${item.unit || ""}`}</small></span>
          <span className="wv2-stock-row__locations"><strong>{item.locations?.[0]?.warehouse_name || "Lieu non renseigné"}</strong><small>{item.locations?.length > 1 ? `${item.locations.length} emplacements · total calculé` : item.reliability?.message}</small></span>
          <span className="wv2-stock-row__money"><strong>{item.financials?.stock_value != null ? formatMoney(item.financials.stock_value) : item.financials ? "À confirmer" : "Masquée"}</strong><small>{item.financials?.value_status === "missing_cost" ? "Prix d'achat manquant" : item.supplier?.name || "Fournisseur non renseigné"}</small></span>
          <ChevronRight size={18} />
        </button>)}
        {!visible.length ? <Empty title="Aucun produit trouvé" note="Modifiez la recherche, la catégorie ou le filtre sélectionné." /> : null}
        <footer>{visible.length} produit{visible.length > 1 ? "s" : ""} affiché{visible.length > 1 ? "s" : ""} sur {items.length}</footer>
      </section>

      {selected ? <WaiseDrawer title={selected.name} eyebrow={`${selected.category || "Stock"} · ${typeLabel(selected)}`} onClose={() => setSelected(null)}>
        <div className="wv2-stock-drawer-state"><div><span>Stock actuel</span><strong>{quantityLabel(selected)}</strong></div><Badge status={selected.status} /></div>
        <p className="wv2-stock-reliability">{selected.reliability?.message || "Fiabilité non renseignée"}{selected.reliability?.updated_at ? ` · mis à jour ${dateLabel(selected.reliability.updated_at)}` : ""}</p>

        <section className="wv2-drawer-block"><h3>Où se trouve ce produit</h3>{selected.locations?.length ? <div className="wv2-location-list">{selected.locations.map((location, index) => <div key={`${location.warehouse_id || index}-${location.warehouse_name}`}><span>{location.warehouse_name || "Lieu sans nom"}</span><strong>{formatNumber(location.quantity)} {selected.unit || "unité"}</strong></div>)}<div className="is-total"><span>Total</span><strong>{quantityLabel(selected)}</strong></div></div> : <div className="wv2-drawer-note"><AlertTriangle size={18} /><span>Aucun lieu de stock n'est associé à ce produit.</span></div>}</section>

        <section className="wv2-drawer-block"><h3>Unités et approvisionnement</h3><dl className="wv2-detail-list"><div><dt>Unité d'achat</dt><dd>{selected.units?.purchase || "Non renseignée"}</dd></div><div><dt>Unité de stockage</dt><dd>{selected.units?.storage || selected.unit || "Non renseignée"}</dd></div><div><dt>Seuil de stock faible</dt><dd>{formatNumber(selected.threshold)} {selected.unit || ""}</dd></div><div><dt>Fournisseur habituel</dt><dd>{selected.supplier?.name || "Non renseigné"}</dd></div>{selected.packaging?.length ? <div><dt>Conditionnements</dt><dd>{selected.packaging.map((entry) => `${entry.label || "Lot"} × ${formatNumber(entry.quantity)}`).join(", ")}</dd></div> : null}</dl></section>

        {selected.financials ? <section className="wv2-drawer-block"><h3>Prix et valeur</h3><dl className="wv2-detail-list"><div><dt>Prix d'achat actuel</dt><dd>{selected.financials.reference_purchase_cost != null ? formatMoney(selected.financials.reference_purchase_cost) : "Manquant"}</dd></div><div><dt>Coût moyen du stock</dt><dd>{selected.financials.average_stock_cost != null ? formatMoney(selected.financials.average_stock_cost) : "Manquant"}</dd></div><div><dt>Valeur du stock</dt><dd>{selected.financials.stock_value != null ? formatMoney(selected.financials.stock_value) : "À confirmer"}</dd></div></dl>{selected.financials.reference_purchase_cost != null && selected.financials.average_stock_cost != null && Math.abs(selected.financials.reference_purchase_cost - selected.financials.average_stock_cost) > 0.0001 ? <div className="wv2-drawer-note"><AlertTriangle size={18} /><span>Le prix d'achat a changé, mais le stock déjà compté conserve son coût moyen. Une erreur d'initialisation peut être corrigée depuis sa fiche dans Configuration.</span></div> : null}<p className={`wv2-confidence wv2-confidence--${selected.financials.value_status === "known" ? "success" : "warning"}`}>{selected.financials.value_status === "known" ? "Valeur calculée sur la quantité actuelle et son coût moyen." : selected.financials.value_status === "missing_cost" ? "Prix manquant : ce produit n'entre pas dans la valeur connue." : "La quantité doit être vérifiée avant de valoriser ce stock."}</p></section> : null}

        <section className="wv2-drawer-block"><h3>Dernières informations enregistrées</h3><dl className="wv2-detail-list"><div><dt>Dernière réception</dt><dd>{dateLabel(selected.activity?.last_receipt_at)}</dd></div><div><dt>Dernière sortie</dt><dd>{dateLabel(selected.activity?.last_output_at)}</dd></div><div><dt>Dernier comptage</dt><dd>{dateLabel(selected.activity?.last_count_at)}</dd></div></dl></section>

        <section className="wv2-drawer-block"><h3>Caisse et recettes</h3><dl className="wv2-detail-list"><div><dt>Lien avec la caisse</dt><dd>{selected.cash_register_link ? `Lié à ${selected.cash_register_link.product_name || `produit #${selected.cash_register_link.product_id}`}` : "Non vendu directement"}</dd></div><div><dt>Utilisation en recette</dt><dd>{selected.recipe_usage?.count ? `${selected.recipe_usage.count} recette${selected.recipe_usage.count > 1 ? "s" : ""}` : "Aucune recette"}</dd></div></dl></section>

        {selected.configuration_issues?.length ? <section className="wv2-drawer-block wv2-drawer-block--warning"><h3>À compléter sur ce produit</h3><ul>{selected.configuration_issues.map((issue) => <li key={issue}>{STOCK_ISSUE_LABELS[issue] || issue}</li>)}</ul>{permissions.can_configure_stock ? <button type="button" className="wv2-secondary" onClick={() => { setSelected(null); onSelect("configuration"); }}>Ouvrir la configuration</button> : null}</section> : null}

        <div className="wv2-stock-drawer-actions">{permissions.can_count_stock ? <button type="button" onClick={() => openWorkflow("inventory")}><ClipboardCheck size={17} />Compter</button> : null}{permissions.can_declare_loss ? <button type="button" onClick={() => openWorkflow("loss")}><Trash2 size={17} />Déclarer une perte</button> : null}{permissions.can_create_purchase_order ? <button type="button" onClick={() => openWorkflow("purchase")}><ShoppingCart size={17} />Créer un bon</button> : null}</div>
      </WaiseDrawer> : null}
    </>
  );
};

const PurchasesView = ({ data, onAction, onReceivePurchase, onRefresh }) => {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [downloadError, setDownloadError] = useState("");
  const [mutationError, setMutationError] = useState("");
  const [busyOrderId, setBusyOrderId] = useState(null);
  const orders = data?.orders || [];
  const visible = orders.filter((order) => {
    if (status !== "all" && order.status !== status) return false;
    const needle = search.trim().toLocaleLowerCase("fr");
    if (!needle) return true;
    return [order.number, order.supplier?.name, order.warehouse?.name, ...(order.lines || []).map((line) => line.name)].filter(Boolean).join(" ").toLocaleLowerCase("fr").includes(needle);
  });
  const download = async (order) => {
    setDownloadError("");
    try {
      await downloadPurchaseOrderPdf(order);
    } catch (error) {
      setDownloadError(error.message);
    }
  };
  const canCancel = (order) => Boolean(
    data?.permissions?.can_cancel_purchase_order &&
    !["cancelled", "received", "completed", "partial"].includes(order?.status) &&
    Number(order?.progress?.handled || 0) === 0
  );
  const canDelete = (order) => Boolean(
    data?.permissions?.can_delete_purchase_order &&
    (order?.status === "draft" || order?.status === "cancelled") &&
    Number(order?.progress?.handled || 0) === 0
  );
  const cancelOrder = async (order) => {
    if (!canCancel(order) || busyOrderId) return;
    if (!window.confirm(`Annuler ${order.number || `le bon #${order.id}`} ? Aucun stock ne sera modifié.`)) return;
    setMutationError("");
    setBusyOrderId(order.id);
    try {
      await mutateWaise(`/api/waise/purchase-orders/${order.id}/cancel`, {
        source:order,
        body: { client_request_id: makeRequestId("cancel-purchase-order") },
      });
      setSelected(null);
      showWaiseToast("success", "Bon d'achat annulé. Le stock est inchangé.");
      onRefresh?.();
    } catch (error) {
      setMutationError(error.message);
    } finally {
      setBusyOrderId(null);
    }
  };
  const deleteOrder = async (order) => {
    if (!canDelete(order) || busyOrderId) return;
    if (!window.confirm(`Supprimer définitivement ${order.number || `le bon #${order.id}`} ? Cette action est réservée aux bons sans réception.`)) return;
    setMutationError("");
    setBusyOrderId(order.id);
    try {
      await mutateWaise(`/api/waise/purchase-orders/${order.id}`, { method: "DELETE",source:order });
      setSelected(null);
      showWaiseToast("success", "Bon d'achat supprimé.");
      onRefresh?.();
    } catch (error) {
      setMutationError(error.message);
    } finally {
      setBusyOrderId(null);
    }
  };

  return <>
    <div className="wv2-kpis">
      <Kpi label="Bons d'achat" value={data?.summary?.total_orders || 0} />
      <Kpi label="À traiter" value={data?.summary?.needs_attention || 0} tone="warning" />
      <Kpi label="Attendus aujourd'hui" value={data?.summary?.expected_today || 0} />
      <Kpi label="Déjà réceptionnés" value={(data?.summary?.by_status?.completed || 0) + (data?.summary?.by_status?.stored || 0)} tone="success" />
    </div>
    <section className="wv2-section wv2-purchases"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Approvisionnement</p><h3>Commandes fournisseurs</h3></div>{data?.permissions?.can_create_purchase_order ? <ActionButton onClick={() => onAction({ type: "purchase" })}>Créer un bon d'achat</ActionButton> : null}</div>
      <div className="wv2-purchase-controls"><div className="wv2-inline-search"><Search size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher un bon, un fournisseur ou un article…" /></div><select aria-label="Filtrer les bons par statut" value={status} onChange={(event) => setStatus(event.target.value)}><option value="all">Tous les états</option>{["draft", "sent", "expected", "overdue", "partial", "received", "completed", "cancelled"].map((entry) => <option value={entry} key={entry}>{STATUS_LABELS[entry]}</option>)}</select></div>
      {downloadError || mutationError ? <div className="wv2-form-error"><AlertTriangle size={17} />{downloadError || mutationError}</div> : null}
      <div className="wv2-purchase-list">
        {visible.map((order) => <article key={order.id} className="wv2-purchase-row"><button type="button" className="wv2-purchase-row__main" onClick={() => setSelected(order)}><span><strong>{order.number || `Bon #${order.id}`}</strong><small>{order.supplier?.name || "Fournisseur non renseigné"} · {order.warehouse?.name || "Lieu non renseigné"}</small></span><Badge status={order.status} /><span className="wv2-purchase-row__progress"><strong>{order.progress?.line_count || 0}</strong><small>article{order.progress?.line_count > 1 ? "s" : ""}</small></span><span className="wv2-row__amount">{order.financials ? formatMoney(order.financials.total_amount) : "Montant masqué"}</span><ChevronRight size={18} /></button><div className="wv2-purchase-row__actions"><button type="button" onClick={() => download(order)} aria-label={`Télécharger ${order.number || `bon ${order.id}`}`}><Download size={16} /> PDF</button>{canCancel(order) ? <button type="button" className="is-danger" disabled={busyOrderId === order.id} onClick={() => cancelOrder(order)}><X size={16} /> Annuler</button> : null}{canDelete(order) ? <button type="button" className="is-danger" disabled={busyOrderId === order.id} onClick={() => deleteOrder(order)}><Trash2 size={16} /> Supprimer</button> : null}{data?.permissions?.can_receive_stock && !["completed", "cancelled"].includes(order.status) ? <button type="button" onClick={() => onReceivePurchase(order)}><ArrowDownToLine size={16} /> Réceptionner</button> : null}</div></article>)}
        {!visible.length ? <Empty title="Aucun bon correspondant" note={orders.length ? "Modifiez la recherche ou le filtre." : "Créez votre premier bon d'achat fournisseur."} /> : null}
      </div>
    </section>
    {selected ? <WaiseDrawer title={selected.number || `Bon #${selected.id}`} eyebrow="Bon d'achat" onClose={() => setSelected(null)}>
      <div className="wv2-drawer-status"><ShoppingCart size={20} /><div><Badge status={selected.status} /><p>{selected.supplier?.name || "Fournisseur non renseigné"}<br />Livraison vers <strong>{selected.warehouse?.name || "lieu non renseigné"}</strong></p></div></div>
      <dl className="wv2-detail-list"><div><dt>Créé par</dt><dd>{selected.author?.display_name || "Auteur inconnu"}</dd></div><div><dt>Commandé le</dt><dd>{selected.dates?.ordered_at ? new Date(selected.dates.ordered_at).toLocaleDateString("fr-FR") : "Non renseigné"}</dd></div><div><dt>Livraison prévue</dt><dd>{selected.dates?.expected_at ? new Date(selected.dates.expected_at).toLocaleDateString("fr-FR") : "Non renseignée"}</dd></div><div><dt>Avancement</dt><dd>{selected.progress?.percentage || 0}%</dd></div></dl>
      <section className="wv2-drawer-block"><h3>Articles commandés</h3><div className="wv2-purchase-detail-lines">{(selected.lines || []).map((line) => <div key={line.id || line.stock_item_id}><span><strong>{line.name}</strong><small>{formatNumber(line.quantities?.ordered)} {line.packaging?.label || line.unit || "unité(s)"}</small></span><strong>{line.financials ? formatMoney(line.financials.total_price) : ""}</strong></div>)}</div></section>
      {selected.notes ? <div className="wv2-drawer-note">{selected.notes}</div> : null}
      {selected.financials ? <dl className="wv2-detail-list"><div><dt>Sous-total</dt><dd>{formatMoney(selected.financials.subtotal)}</dd></div><div><dt>Frais</dt><dd>{formatMoney(selected.financials.fee_total)}</dd></div><div><dt>Total</dt><dd>{formatMoney(selected.financials.total_amount)}</dd></div></dl> : null}
      <div className="wv2-purchase-drawer-actions"><button type="button" className="wv2-secondary" onClick={() => download(selected)}><Download size={17} /> Télécharger le PDF</button>{data?.permissions?.can_receive_stock && !["completed", "cancelled"].includes(selected.status) ? <button type="button" className="wv2-primary" onClick={() => { setSelected(null); onReceivePurchase(selected); }}><ArrowDownToLine size={17} /> Réceptionner</button> : null}</div>
      {canCancel(selected) || canDelete(selected) ? <div className="wv2-purchase-danger-actions">{canCancel(selected) ? <button type="button" disabled={busyOrderId === selected.id} onClick={() => cancelOrder(selected)}><X size={16} /> Annuler le bon</button> : null}{canDelete(selected) ? <button type="button" className="is-delete" disabled={busyOrderId === selected.id} onClick={() => deleteOrder(selected)}><Trash2 size={16} /> Supprimer</button> : null}</div> : null}
    </WaiseDrawer> : null}
  </>;
};

const InventoriesView = ({ data, onAction, onOpenInventory }) => (
  <>
    <div className="wv2-kpis">
      <Kpi label="Inventaires" value={data?.summary?.total_inventories || 0} />
      <Kpi label="En cours" value={data?.summary?.active || 0} tone="warning" />
      <Kpi label="À revérifier" value={data?.summary?.requiring_movement_review || 0} tone="danger" note="Stock modifié pendant le comptage" />
    </div>
    <section className="wv2-section"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Comptages</p><h3>Inventaires récents</h3></div>{data?.permissions?.can_count_stock ? <ActionButton onClick={() => onAction("inventory")}>Démarrer</ActionButton> : null}</div>
      <div className="wv2-list">
        {(data?.inventories || []).map((inventory) => <button type="button" className="wv2-row wv2-row--button" key={inventory.id} onClick={() => onOpenInventory(inventory.id)}>
          <div className="wv2-row__main"><strong>{inventory.name}</strong><small>{inventory.warehouse?.name || "Lieu inconnu"} · {inventory.coverage?.counted_items || 0}/{inventory.coverage?.total_items || 0} comptés</small></div>
          {inventory.movement_guard?.requires_review ? <Badge status="negative">Mouvements à vérifier</Badge> : <Badge status={inventory.status} />}
        </button>)}
        {!data?.inventories?.length ? <Empty title="Aucun inventaire" note="Les comptages ouverts et terminés apparaîtront ici." /> : null}
      </div>
    </section>
  </>
);

const LossesView = ({ data, onAction, onRefresh }) => {
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const review = async (decision) => {
    if (!selected || busy) return;
    let reason = null;
    if (decision === "refuse") {
      reason = window.prompt("Pourquoi refusez-vous cette perte ?");
      if (!reason?.trim()) return;
    } else if (!window.confirm("Valider cette perte et diminuer le stock officiel ?")) {
      return;
    }
    setBusy(true);
    try {
      await mutateWaise(`/api/waise/loss-reports/${selected.id}/${decision}`, {
        source:selected,
        body: {
          reason: reason?.trim() || null,
          client_request_id: makeRequestId(`waise-loss-${decision}`),
        },
      });
      showWaiseToast(
        "success",
        decision === "validate"
          ? "Perte validée. Le stock officiel a été mis à jour."
          : "Perte refusée. Le stock n'a pas changé.",
      );
      setSelected(null);
      onRefresh?.();
    } catch (reviewError) {
      showWaiseToast("error", reviewError.message);
    } finally {
      setBusy(false);
    }
  };
  const statusLabel = (status) => ({ pending: "En attente", validated: "Validée", refused: "Refusée" }[status] || status);
  return <>
    <div className="wv2-kpis">
      <Kpi label="Déclarations" value={data?.summary?.declarations || 0} />
      <Kpi label="À valider" value={data?.summary?.pending || 0} tone={data?.summary?.pending ? "warning" : "success"} />
      <Kpi label="Valeur connue" value={data?.summary?.valuation ? formatMoney(data.summary.valuation.known_value) : "Masquée"} note={data?.summary?.valuation && !data.summary.valuation.complete ? `${data.summary.valuation.missing_cost_count} coûts manquants` : ""} />
    </div>
    <section className="wv2-section"><div className="wv2-section__head"><div><p className="wv2-eyebrow">À traiter puis historique</p><h3>Pertes déclarées</h3></div>{data?.permissions?.can_declare_loss ? <ActionButton onClick={() => onAction("loss")}>Déclarer</ActionButton> : null}</div>
      <div className="wv2-list">
        {(data?.losses || []).map((loss) => <button type="button" className="wv2-row wv2-row--button" key={`${loss.status}-${loss.id}`} onClick={() => setSelected(loss)}>
          <div className="wv2-row__main"><strong>{loss.item?.name || "Produit"}</strong><small>{loss.loss_type || "Perte"} · {loss.warehouse?.name || "Lieu inconnu"} · {loss.declared_by?.display_name || "Auteur inconnu"}</small></div>
          <Badge status={loss.status}>{statusLabel(loss.status)}</Badge>
          <span>{formatNumber(loss.quantity)} {loss.item?.unit || ""}</span>
          <div className="wv2-row__amount">{loss.financials?.value != null ? formatMoney(loss.financials.value) : "Valeur inconnue"}</div>
        </button>)}
        {!data?.losses?.length ? <Empty title="Aucune perte enregistrée" note="Une absence de déclaration ne signifie pas forcément zéro gaspillage." /> : null}
      </div>
    </section>
    {selected ? <WaiseDrawer title={selected.item?.name || "Perte déclarée"} eyebrow="Traçabilité" onClose={() => setSelected(null)} busy={busy}>
      <div className="wv2-drawer-status"><Trash2 size={20} /><div><Badge status={selected.status}>{statusLabel(selected.status)}</Badge><p>{formatNumber(selected.quantity)} {selected.item?.unit || ""} · {selected.warehouse?.name || "Lieu inconnu"}</p></div></div>
      <dl className="wv2-detail-list">
        <div><dt>Déclarée par</dt><dd>{selected.declared_by?.display_name || "Auteur inconnu"}</dd></div>
        <div><dt>Type</dt><dd>{selected.loss_type || "Perte"}</dd></div>
        <div><dt>Cause</dt><dd>{selected.loss_cause || "Non renseignée"}</dd></div>
        <div><dt>Valeur</dt><dd>{selected.financials?.value != null ? formatMoney(selected.financials.value) : "Prix d'achat manquant"}</dd></div>
      </dl>
      {selected.notes ? <div className="wv2-drawer-note">{selected.notes}</div> : null}
      {selected.status === "pending" && data?.permissions?.can_validate_loss ? <div className="wv2-purchase-drawer-actions"><button type="button" className="wv2-secondary" disabled={busy} onClick={() => review("refuse")}>Refuser</button><button type="button" className="wv2-primary" disabled={busy} onClick={() => review("validate")}>Valider la perte</button></div> : null}
      {selected.status === "pending" && !data?.permissions?.can_validate_loss ? <div className="wv2-drawer-note">En attente de validation par un autre manager ou administrateur.</div> : null}
    </WaiseDrawer> : null}
  </>;
};

const AnalysisView = ({ data, analysisFilters, setAnalysisFilters }) => {
  const [showConsumption, setShowConsumption] = useState(false);
  const [marginPage, setMarginPage] = useState(1);
  const [marginPageSize, setMarginPageSize] = useState(10);
  const [consumptionPage, setConsumptionPage] = useState(1);
  const [consumptionPageSize, setConsumptionPageSize] = useState(10);
  const consumption = data?.consumption || {};
  const margins = data?.margins || null;
  const marginEntries = margins?.top_entities || [];
  const consumptionEntries = consumption?.top_items || [];
  const visibleMarginEntries = marginEntries.slice((marginPage - 1) * marginPageSize, marginPage * marginPageSize);
  const visibleConsumptionEntries = consumptionEntries.slice((consumptionPage - 1) * consumptionPageSize, consumptionPage * consumptionPageSize);
  useEffect(() => {
    setMarginPage((current) => Math.min(current, Math.max(1, Math.ceil(marginEntries.length / marginPageSize))));
  }, [marginEntries.length, marginPageSize]);
  useEffect(() => {
    setConsumptionPage((current) => Math.min(current, Math.max(1, Math.ceil(consumptionEntries.length / consumptionPageSize))));
  }, [consumptionEntries.length, consumptionPageSize]);
  useEffect(() => {
    setMarginPage(1);
    setConsumptionPage(1);
  }, [analysisFilters.category, analysisFilters.end_date, analysisFilters.period_days, analysisFilters.start_date, analysisFilters.stock_item_id, analysisFilters.warehouse_id]);
  const updatePeriod = (value) => {
    if (value !== "custom") {
      setAnalysisFilters((current) => ({ ...current, period_days: value, start_date: "", end_date: "" }));
      return;
    }
    const endDate = new Date().toISOString().slice(0, 10);
    const startDate = new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    setAnalysisFilters((current) => ({
      ...current,
      period_days: "custom",
      start_date: current.start_date || startDate,
      end_date: current.end_date || endDate,
    }));
  };
  return <>
    <section className="wv2-pulse wv2-pulse--analysis"><div className="wv2-pulse__ring"><span /></div><div><p className="wv2-eyebrow">Lecture de la période</p><h2>{data?.verdict || "Analyse du stock"}</h2><p>{data?.reliability?.message || "Les conclusions distinguent les données certaines des estimations."}</p></div></section>
    <section className="wv2-section wv2-analysis-filters"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Périmètre de lecture</p><h3>Quelles données voulez-vous analyser ?</h3><small>La période filtre les consommations, pertes, achats et inventaires. L’état et la valeur du stock montrent toujours la situation actuelle.</small></div><button type="button" className="wv2-secondary" onClick={() => setAnalysisFilters({ period_days: "30", start_date: "", end_date: "", warehouse_id: "", category: "", stock_item_id: "" })}>Réinitialiser</button></div><div className="wv2-form-grid"><Field label="Période"><select value={analysisFilters.period_days} onChange={(event) => updatePeriod(event.target.value)}><option value="1">Aujourd'hui</option><option value="7">7 derniers jours</option><option value="30">30 derniers jours</option><option value="90">90 derniers jours</option><option value="365">12 derniers mois</option><option value="custom">Dates personnalisées</option></select></Field><Field label="Entrepôt"><select value={analysisFilters.warehouse_id} onChange={(event) => setAnalysisFilters((current) => ({ ...current, warehouse_id: event.target.value }))}><option value="">Tous les lieux</option>{(data?.filter_options?.warehouses || []).map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}</select></Field><Field label="Famille"><select value={analysisFilters.category} onChange={(event) => setAnalysisFilters((current) => ({ ...current, category: event.target.value }))}><option value="">Toutes les familles</option>{(data?.filter_options?.categories || []).map((category) => <option key={category} value={category}>{category}</option>)}</select></Field><Field label="Produit"><select value={analysisFilters.stock_item_id} onChange={(event) => setAnalysisFilters((current) => ({ ...current, stock_item_id: event.target.value }))}><option value="">Tous les produits</option>{(data?.filter_options?.products || []).map((item) => <option key={item.id} value={item.id}>{item.label || item.name}</option>)}</select></Field>{analysisFilters.period_days === "custom" ? <><Field label="Date de début"><input type="date" value={analysisFilters.start_date || ""} max={analysisFilters.end_date || undefined} onChange={(event) => setAnalysisFilters((current) => ({ ...current, start_date: event.target.value }))} /></Field><Field label="Date de fin"><input type="date" value={analysisFilters.end_date || ""} min={analysisFilters.start_date || undefined} onChange={(event) => setAnalysisFilters((current) => ({ ...current, end_date: event.target.value }))} /></Field></> : null}</div></section>
    <div className="wv2-kpis">
      <Kpi label="Valeur connue" value={data?.financials ? formatMoney(data.financials.stock_value_known) : "Masquée"} />
      <Kpi label="Pertes connues" value={data?.financials ? formatMoney(data.financials.loss_value_known) : "Masquées"} tone="danger" />
      <Kpi label="Articles sans prix" value={data?.reliability?.missing_cost_count || 0} tone="warning" />
      <Kpi label="Stocks à vérifier" value={data?.reliability?.unreliable_quantity_count || 0} tone="warning" />
    </div>
    {margins ? <section className="wv2-section"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Ventes et coûts historiques</p><h3>Marge théorique avant remises</h3><small>{margins.message}</small></div><Badge status={margins.complete ? "ok" : "low_stock"}>{margins.complete ? "Tous les coûts sont connus" : `${margins.missing_cost_entity_count || 0} article${margins.missing_cost_entity_count !== 1 ? "s" : ""} sans coût historique`}</Badge></div>
      <div className="wv2-kpis"><Kpi label="Ventes au tarif" value={formatMoney(margins.revenue)} note="Chiffre d'affaires complet" /><Kpi label="Ventes avec coût connu" value={formatMoney(margins.known_revenue)} note={margins.coverage_rate == null ? "Couverture indisponible" : `${formatNumber(margins.coverage_rate)} % du chiffre d'affaires`} tone={margins.complete ? "success" : "warning"} /><Kpi label="Coût historique connu" value={formatMoney(margins.known_cost)} note={`${margins.missing_cost_count || 0} vente${margins.missing_cost_count !== 1 ? "s" : ""} sans coût historique`} /><Kpi label="Marge calculable" value={formatMoney(margins.known_margin)} note={margins.margin_rate == null ? "Taux indisponible" : `${formatNumber(margins.margin_rate)} % sur les ventes avec coût connu`} /></div>
      <div className="wv2-list">{visibleMarginEntries.map((entry) => <article className="wv2-row" key={`${entry.source_type}-${entry.entity_id || entry.name}`}><div className="wv2-row__main"><strong>{entry.name}</strong><small>{entry.source_type === "extra" ? "Supplément vendu" : "Produit vendu"} · CA {formatMoney(entry.revenue)}{entry.included_extra_cost > 0 ? ` · dont ${formatMoney(entry.included_extra_cost)} d'accompagnement inclus` : ""}{entry.missing_cost_count ? ` · ${entry.missing_cost_count} coût(s) manquant(s)` : ""}</small></div><div className="wv2-row__amount">{entry.known_revenue > 0 ? formatMoney(entry.known_margin) : "Marge inconnue"}</div></article>)}{!marginEntries.length ? <Empty title="Aucune vente analysable" note="Les marges apparaîtront après une vente payée et un impact WAISE enregistré." /> : null}</div>
      <PaginatedListFooter page={marginPage} pageSize={marginPageSize} total={marginEntries.length} onPageChange={setMarginPage} onPageSizeChange={(size) => { setMarginPageSize(size); setMarginPage(1); }} />
    </section> : null}
    <section className="wv2-section"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Utilisation réelle</p><h3>Consommation sur la période</h3></div><button type="button" className="wv2-secondary" onClick={() => setShowConsumption(true)}>Voir l'historique</button></div>
      <div className="wv2-kpis"><Kpi label="Sorties de consommation" value={consumption.movement_count || 0} note="Les pertes sont comptées séparément" /><Kpi label="Coût consommé connu" value={consumption.known_value == null ? "Masqué" : formatMoney(consumption.known_value)} note={consumption.missing_cost_count ? `${consumption.missing_cost_count} coût(s) manquant(s)` : "Coûts historiques enregistrés"} /></div>
      <div className="wv2-list">{visibleConsumptionEntries.map((entry) => <article className="wv2-row" key={entry.item.id}><div className="wv2-row__main"><strong>{entry.item.name}</strong><small>{formatNumber(entry.quantity)} {entry.item.unit} consommé(s)</small></div>{data?.permissions?.can_view_financials ? <div className="wv2-row__amount">{formatMoney(entry.total_cost)}</div> : null}</article>)}{!consumptionEntries.length ? <Empty title="Aucune consommation sur cette période" note="Les ventes liées aux recettes apparaîtront ici, sans mélanger les pertes." /> : null}</div>
      <PaginatedListFooter page={consumptionPage} pageSize={consumptionPageSize} total={consumptionEntries.length} onPageChange={setConsumptionPage} onPageSizeChange={(size) => { setConsumptionPageSize(size); setConsumptionPage(1); }} />
    </section>
    <section className="wv2-section"><div className="wv2-section__head"><div><p className="wv2-eyebrow">À vérifier</p><h3>Alertes et données à compléter</h3></div></div><div className="wv2-list">{(data?.findings || []).map((finding, index) => <article className="wv2-row" key={`${finding.code}-${index}`}><span className={`wv2-dot wv2-dot--${finding.severity}`} /><div className="wv2-row__main"><strong>{finding.title}</strong><small>{finding.message}</small></div></article>)}{!data?.findings?.length ? <Empty title="Aucune alerte ni donnée manquante" note="Les informations nécessaires à cette lecture sont renseignées." /> : null}</div></section>
    <section className="wv2-section"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Origine des chiffres</p><h3>Sources utilisées</h3></div></div><div className="wv2-list">{(data?.sources || []).map((source) => <article className="wv2-row" key={source.id}><CheckCircle2 size={18} /><div className="wv2-row__main"><strong>{source.label}</strong><small>Écriture locale enregistrée</small></div></article>)}</div><div className="wv2-drawer-note wv2-comparison-note">{data?.comparison?.message}</div></section>
    {showConsumption ? <WaiseDrawer title="Historique de consommation" eyebrow="Ventes, recettes et sorties" onClose={() => setShowConsumption(false)} wide><div className="wv2-drawer-note">Les pertes validées ne sont jamais incluses ici : elles restent dans la page Pertes.</div><div className="wv2-list">{(consumption.history || []).map((movement) => <article className="wv2-row" key={movement.id}><div className="wv2-row__main"><strong>{movement.item.name}</strong><small>{movement.date ? new Date(movement.date).toLocaleString("fr-FR") : "Date inconnue"} · {movement.source}<br />{formatNumber(movement.quantity)} {movement.item.unit} · {movement.warehouse.name} · {movement.user}</small></div>{movement.financials ? <div className="wv2-row__amount"><strong>{movement.financials.total_cost == null ? "Coût consommé inconnu" : `${movement.financials.reliability === "estimated" ? "Coût consommé estimé" : "Coût consommé"} : ${formatMoney(movement.financials.total_cost)}`}</strong><small>{movement.financials.unit_cost == null ? "Prix d'achat absent" : `${movement.financials.reliability === "estimated" ? "Coût actuel utilisé" : "Coût unitaire historique"} : ${formatMoney(movement.financials.unit_cost)} / ${movement.item.unit}`}</small></div> : null}</article>)}{!consumption.history?.length ? <Empty title="Aucun mouvement" note="Modifiez la période ou les filtres pour élargir la recherche." /> : null}</div></WaiseDrawer> : null}
  </>
};

const DailySituationView = ({ data, dailyDate, setDailyDate, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const [warehouseId, setWarehouseId] = useState("");
  const allRows = useMemo(() => data?.rows || [], [data?.rows]);
  const warehouses = useMemo(() => [...new Map(allRows.map((row) => [Number(row.warehouse_id), { id: Number(row.warehouse_id), name: row.warehouse_name }])).values()], [allRows]);
  const rows = warehouseId ? allRows.filter((row) => Number(row.warehouse_id) === Number(warehouseId)) : allRows;
  const summary = data?.summary || {};
  const statusLabel = data?.status === "closed"
    ? "Clôturée"
    : data?.status === "ready"
      ? "À clôturer"
      : data?.status === "provisional"
        ? "Provisoire"
        : "En cours";
  const finalize = async () => {
    setBusy(true);
    try {
      await mutateWaise("/api/waise/v2/daily-situation/finalize", {
        source:data,
        method: "POST",
        body: { date: data?.business_date },
      });
      showWaiseToast("success", "Situation clôturée.");
      onRefresh?.();
    } catch (error) {
      showWaiseToast("error", error.message);
    } finally {
      setBusy(false);
    }
  };
  return <>
    <section className="wv2-section wv2-daily-controls"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Journée d'exploitation</p><h3>{data?.period?.label || data?.business_date || "Situation du stock"}</h3><small>{data?.period?.timeWindowLabel || "Ouverture, mouvements et clôture"}</small></div><div className="wv2-actions"><input aria-label="Date de la situation" type="date" value={dailyDate || data?.business_date || ""} min={data?.config?.activation_service_date || undefined} onChange={(event) => setDailyDate(event.target.value)} />{data?.warehouse_filter_available ? <select aria-label="Lieu de stock" value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)}><option value="">Tous les lieux</option>{warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}</select> : null}{data?.status === "ready" ? <button type="button" className="wv2-primary" disabled={busy || !commandAvailable("daily-situation.finalize",true)} onClick={finalize}>{busy ? <Loader2 size={17} className="is-spinning" /> : <ShieldCheck size={17} />} Clôturer</button> : <Badge status={data?.status === "closed" ? "ok" : "in_progress"}>{statusLabel}</Badge>}</div></div></section>
    <div className="wv2-kpis">
      <Kpi label="Stock initial" value={summary.opening_value == null ? "Valeur incomplète" : formatMoney(summary.opening_value)} />
      <Kpi label="Stock final" value={summary.closing_value == null ? "Valeur incomplète" : formatMoney(summary.closing_value)} />
      <Kpi label="Articles valorisés" value={`${summary.valued_item_count || 0} / ${summary.item_count || 0}`} tone={summary.valuation_complete ? "success" : "warning"} />
      <Kpi label="Coûts manquants" value={summary.missing_cost_item_count || 0} tone={summary.missing_cost_item_count ? "warning" : "success"} />
    </div>
    {data?.pending_reserve_jobs ? <div className="wv2-form-error"><AlertTriangle size={17} />Déstockage en attente : situation provisoire.</div> : null}
    <section className="wv2-section"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Détail</p><h3>Ouverture → mouvements → clôture</h3></div><div className="wv2-actions"><button type="button" className="wv2-secondary" disabled={!allRows.length} onClick={() => exportDailySituationWorkbook(data).catch((error) => showWaiseToast("error", error.message))}><Download size={17} /> Excel</button><button type="button" className="wv2-secondary" disabled={!allRows.length} onClick={() => { try { printDailySituation(data); } catch (error) { showWaiseToast("error", error.message); } }}><Download size={17} /> Imprimer</button></div></div>
      <div className="wv2-list">{rows.map((row) => <article className="wv2-row wv2-daily-row" key={`${row.stock_item_id}-${row.warehouse_id}`}><div className="wv2-row__main"><strong>{row.item_name}</strong><small>{row.warehouse_name} · {row.category}</small><small>{dailyMovementSummary(row)}</small></div><div><small>Initial</small><strong>{formatNumber(row.opening_quantity)} {row.unit}</strong></div><div><small>Net</small><strong>{formatNumber(row.entries + row.production + row.adjustments - row.outputs - row.losses - row.transfers)} {row.unit}</strong></div><div><small>Final</small><strong>{formatNumber(row.closing_quantity)} {row.unit}</strong></div><div className="wv2-row__amount"><small>Valeur finale</small><strong>{row.closing_value == null ? "Non valorisée" : formatMoney(row.closing_value)}</strong></div></article>)}{!rows.length ? <Empty title="Aucun stock à afficher" note="Les articles apparaîtront après leur initialisation." /> : null}</div>
    </section>
  </>;
};

const StockItemConfigurationDrawer = ({ record, costsOnly = false, initialType = "ingredient", resources, onClose, onDone }) => {
  const openingInputs=useRef(null);
  if(!openingInputs.current || openingInputs.current.id!==record?.id) openingInputs.current={id:record?.id,record,resources};
  const [source, setSource] = useState(record || null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(Boolean(record?.id));
  const [busy, setBusy] = useState(false);
  const [revaluing, setRevaluing] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({});
  const [revaluationPreview, setRevaluationPreview] = useState(null);
  const [revaluationReason, setRevaluationReason] = useState("");

  useEffect(() => {
    let active = true;
    const {record,resources}=openingInputs.current;
    Promise.all([fetchWaise("/api/waise/stock-items"), fetchWaise("/api/waise/units")])
      .then(([items, nextUnits]) => {
        if (!active) return;
        const full = record?.id ? (items || []).find((item) => Number(item.id) === Number(record.id)) || record : record;
        const firstWarehouse = resources?.warehouses?.[0]?.id || "";
        setSource(full || null);
        setUnits(Array.isArray(nextUnits) ? nextUnits : []);
        const packagingQuantity = Number(full?.packaging?.[0]?.packaging_qty ?? full?.packaging?.[0]?.quantity ?? 1);
        const packagingPrice = full?.packaging?.[0]?.purchase_price ?? "";
        const calculatedUnitCost = Number(packagingQuantity) > 0 && Number(packagingPrice) > 0
          ? Math.round((Number(packagingPrice) / Number(packagingQuantity)) * 10000) / 10000
          : full?.cost_price ?? full?.weighted_avg_cost ?? "";
        setForm({
          name: full?.name || "",
          category: full?.category || "",
          type: full?.type || initialType,
          warehouse_id: full?.warehouse_id || firstWarehouse,
          unit_id: full?.unit_id || "",
          supplier_id: full?.supplier_id || findGeneralSupplierId(resources?.suppliers),
          cost_price: calculatedUnitCost,
          minimum_stock: full?.minimum_stock ?? 0,
          maximum_stock: full?.maximum_stock ?? 0,
          reorder_point: full?.reorder_point ?? 0,
          reorder_quantity: full?.reorder_quantity ?? 0,
          packaging_label: full?.packaging?.[0]?.label || full?.packaging?.[0]?.packaging_unit || "",
          packaging_qty: packagingQuantity,
          packaging_price: packagingPrice,
        });
        setLoading(false);
      })
      .catch((loadError) => { if (active) { setError(loadError.message); setLoading(false); } });
    return () => { active = false; };
  }, [initialType, record?.id]);

  useEffect(() => {
    let active = true;
    if (!record?.id) {
      setRevaluationPreview(null);
      return () => { active = false; };
    }
    fetchWaise(`/api/waise/stock-items/${record.id}/revaluation-preview`)
      .then((preview) => { if (active) setRevaluationPreview(preview); })
      .catch(() => { if (active) setRevaluationPreview(null); });
    return () => { active = false; };
  }, [record?.id]);

  const update = (key) => (event) => {
    const value = event.target.value;
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (["packaging_qty", "packaging_price"].includes(key)) {
        const quantity = Number(next.packaging_qty);
        const lotPrice = Number(next.packaging_price);
        next.cost_price = quantity > 0 && lotPrice > 0
          ? Math.round((lotPrice / quantity) * 10000) / 10000
          : "";
      }
      return next;
    });
  };
  const save = async (event) => {
    event.preventDefault();
    if (!form.name?.trim()) return setError("Le nom du produit est obligatoire.");
    if (!Number(form.warehouse_id)) return setError("Choisissez un lieu de stockage.");
    if (!Number(form.unit_id)) return setError("Choisissez une unité de stockage.");
    let purchase;
    try {
      purchase = normalizeOptionalPackaging({
        label: form.packaging_label,
        quantity: form.packaging_qty,
        price: form.packaging_price,
      });
    } catch (validationError) {
      return setError(validationError.message);
    }
    setBusy(true);
    setError("");
    try {
      const body = {
        ...(source || {}),
        ...(isRemoteCloudHost(window.location?.hostname) && purchase.packaging ? {_waise_packaging:[purchase.packaging]} : {}),
        name: form.name.trim(), category: form.category || null, type: form.type,
        warehouse_id: Number(form.warehouse_id), unit_id: Number(form.unit_id),
        supplier_id: Number(form.supplier_id) || null,
        cost_price: purchase.packaging
          ? purchase.costPrice
          : (record?.id ? (source?.cost_price ?? source?.weighted_avg_cost ?? null) : null),
        minimum_stock: Number(form.minimum_stock) || 0,
        maximum_stock: Number(form.maximum_stock) || 0,
        reorder_point: Number(form.reorder_point) || 0,
        reorder_quantity: Number(form.reorder_quantity) || 0,
        is_active: 1,
      };
      const saved = await mutateWaise(record?.id ? `/api/waise/stock-items/${record.id}` : "/api/waise/stock-items", {
        method: record?.id ? "PUT" : "POST", body,source,
      });
      const savedId = Number(record?.id || saved?.id);
      if (savedId && purchase.packaging && !isRemoteCloudHost(window.location?.hostname)) {
        await mutateWaise(`/api/waise/stock-items/${savedId}/packaging`, { method: "PUT",source:saved, body: {
          packaging: [purchase.packaging],
          alert_threshold: Number(form.minimum_stock) || 0,
        } });
      }
      showWaiseToast("success", record?.id ? "Produit mis à jour." : "Produit ajouté au suivi Waise.");
      onDone();
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!record?.id || !window.confirm(`Supprimer « ${record.name} » du suivi Waise ?`)) return;
    setBusy(true); setError("");
    try {
      await mutateWaise(`/api/waise/stock-items/${record.id}`, { method: "DELETE",source });
      showWaiseToast("success", "Produit supprimé du suivi Waise.");
      onDone();
    } catch (removeError) {
      setError(removeError.message);
    } finally { setBusy(false); }
  };

  const revalueCurrentStock = async () => {
    if (!record?.id || !revaluationPreview?.revaluation_required) return;
    if (revaluationReason.trim().length < 5) {
      setError("Indiquez pourquoi le coût initial du stock doit être corrigé.");
      return;
    }
    const accepted = window.confirm(
      `Corriger la valeur actuelle de « ${record.name} » de ${formatMoney(revaluationPreview.current_stock_value)} à ${formatMoney(revaluationPreview.corrected_stock_value)} ?\n\nLa quantité ne changera pas et une trace d'audit sera conservée.`,
    );
    if (!accepted) return;
    setRevaluing(true);
    setError("");
    try {
      const result = await mutateWaise(`/api/waise/stock-items/${record.id}/revalue`, {
        source,
        body: {
          reason: revaluationReason.trim(),
          client_request_id: makeRequestId(`waise-revalue-${record.id}`),
        },
      });
      showWaiseToast(
        "success",
        `Valeur corrigée : ${formatMoney(result.corrected_stock_value)} pour ${formatNumber(result.quantity)} unité(s).`,
      );
      onDone();
    } catch (revaluationError) {
      setError(revaluationError.message);
    } finally {
      setRevaluing(false);
    }
  };

  const creationTitle = initialType === "consumable" ? "Nouveau consommable" : "Nouvel ingrédient";
  const isCatalogProduct = Boolean(source?.product_id || record?.product_id || source?.extra_id || record?.extra_id);
  return <WaiseDrawer title={record?.name || creationTitle} eyebrow={costsOnly ? "Prix d'achat et conditionnement" : "Configuration du stock"} onClose={onClose} busy={busy} wide>
    {error ? <div className="wv2-form-error"><AlertTriangle size={17} />{error}</div> : null}
    {loading ? <div className="wv2-loading"><Loader2 className="is-spinning" /><span>Chargement du produit…</span></div> : <form className="wv2-form" onSubmit={save}>
      <Field label={isCatalogProduct ? "Produit du catalogue MOO Sync" : "Nom"}>
        <input value={form.name || ""} onChange={update("name")} readOnly={isCatalogProduct} />
        {isCatalogProduct ? <small>Le nom se modifie depuis Produits dans le tableau de bord MOO Sync.</small> : null}
      </Field>
      {!costsOnly ? <>
        {!isCatalogProduct ? <div className="wv2-form-grid"><Field label="Nature"><select value={form.type || "ingredient"} onChange={update("type")}><option value="ingredient">Ingrédient</option><option value="consumable">Consommable</option></select></Field><Field label="Famille"><input value={form.category || ""} onChange={update("category")} /></Field></div> : null}
        <div className="wv2-form-grid"><Field label="Lieu de stockage"><select value={form.warehouse_id || ""} onChange={update("warehouse_id")}><option value="">Choisir…</option>{(resources?.warehouses || []).map((entry) => <option value={entry.id} key={entry.id}>{entry.name}</option>)}</select></Field><Field label="Unité de stockage"><select value={form.unit_id || ""} onChange={update("unit_id")}><option value="">Choisir…</option>{units.filter((entry) => entry.is_active !== 0).map((entry) => <option value={entry.id} key={entry.id}>{entry.name} ({entry.symbol})</option>)}</select></Field></div>
        <Field label="Fournisseur habituel"><select value={form.supplier_id || ""} onChange={update("supplier_id")}><option value="">Aucun</option>{(resources?.suppliers || []).map((entry) => <option value={entry.id} key={entry.id}>{entry.name}</option>)}</select></Field>
      </> : null}
      <div className="wv2-form-grid"><Field label="Prix unitaire"><input type="number" min="0" step="0.0001" value={form.cost_price ?? ""} placeholder="Non renseigné" readOnly /></Field><Field label="Seuil faible"><input type="number" min="0" step="0.001" value={form.minimum_stock ?? 0} onChange={update("minimum_stock")} /></Field></div>
      {revaluationPreview?.revaluation_required ? <section className="wv2-drawer-block wv2-drawer-block--warning">
        <div className="wv2-section__head"><div><p className="wv2-eyebrow">Stock déjà compté</p><h3>Le coût actuel est resté sur l'ancien prix</h3></div><AlertTriangle size={20} /></div>
        <dl className="wv2-detail-list">
          <div><dt>Quantité actuelle</dt><dd>{formatNumber(revaluationPreview.quantity)}</dd></div>
          <div><dt>Coût moyen enregistré</dt><dd>{formatMoney(revaluationPreview.current_unit_cost)}</dd></div>
          <div><dt>Nouveau prix unitaire</dt><dd>{formatMoney(revaluationPreview.reference_unit_cost)}</dd></div>
          <div><dt>Valeur actuelle</dt><dd>{formatMoney(revaluationPreview.current_stock_value)}</dd></div>
          <div><dt>Valeur après correction</dt><dd>{formatMoney(revaluationPreview.corrected_stock_value)}</dd></div>
        </dl>
        <p>Utilisez cette correction uniquement pour réparer une erreur d'initialisation. Un changement normal de prix fournisseur doit rester applicable aux prochains achats.</p>
        <Field label="Motif obligatoire"><textarea value={revaluationReason} onChange={(event) => setRevaluationReason(event.target.value)} placeholder="Ex. Mauvais prix saisi lors du premier comptage" rows="3" /></Field>
        <button type="button" className="wv2-secondary" onClick={revalueCurrentStock} disabled={busy || revaluing || !commandAvailable("stock-item.revalue",true)}>{revaluing ? <Loader2 size={17} className="is-spinning" /> : <RefreshCw size={17} />} Corriger la valeur du stock existant</button>
      </section> : null}
      {!costsOnly ? <div className="wv2-form-grid"><Field label="Stock maximum"><input type="number" min="0" step="0.001" value={form.maximum_stock ?? 0} onChange={update("maximum_stock")} /></Field><Field label="Point de commande"><input type="number" min="0" step="0.001" value={form.reorder_point ?? 0} onChange={update("reorder_point")} /></Field></div> : null}
      <section className="wv2-recipe-components"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Achat</p><h3>Conditionnement (facultatif)</h3></div></div><div className="wv2-form-grid"><Field label="Nom du lot"><input value={form.packaging_label || ""} onChange={update("packaging_label")} placeholder="Carton, casier…" /></Field><Field label="Unités"><input type="number" min="1" step="1" value={form.packaging_qty ?? 1} onChange={update("packaging_qty")} /></Field></div><Field label="Prix du lot"><input type="number" min="0" step="0.01" value={form.packaging_price ?? ""} onChange={update("packaging_price")} /></Field></section>
      <footer>{record?.id ? <button type="button" className="wv2-secondary wv2-danger-action" onClick={remove} disabled={busy || !commandAvailable("stock-item.delete",true)}>Supprimer</button> : <button type="button" className="wv2-secondary" onClick={onClose}>Annuler</button>}<button type="submit" className="wv2-primary" disabled={busy || !commandAvailable(record?.id ? "stock-item.update" : "stock-item.create",Boolean(record?.id))}>{busy ? <Loader2 size={17} className="is-spinning" /> : <Save size={17} />} Enregistrer</button></footer>
    </form>}
  </WaiseDrawer>;
};

const ConfigurationPanelDrawer = ({ panel, data, onClose, onAction, onStockItem, onExtra, onExtraTracking, ensuringExtraId }) => {
  const [search, setSearch] = useState("");
  const resources = data?.resources || {};
  const canConfigure = Boolean(data?.permissions?.can_configure_stock);
  const needle = search.trim().toLocaleLowerCase("fr");
  const matches = (entry) => !needle || [entry?.name, entry?.category, entry?.warehouse_name, entry?.supplier_name, entry?.variation_name].filter(Boolean).join(" ").toLocaleLowerCase("fr").includes(needle);
  // The parent drawer stays mounted below its editor so saving or cancelling
  // returns to the same list instead of ejecting the user to Configuration.
  const openChild = (action) => action();
  const closeAnd = openChild;
  const titles = { resold: "Produits revendus", ingredients: "Ingrédients", recipes: "Recettes", suppliers: "Fournisseurs", losses: "Réglages des pertes", warehouses: "Lieux de stockage" };
  const resoldEntries = [
    ...(resources.stock_items || []).filter((entry) => entry.product_id && entry.tracked_product && entry.inventory_kind === "resold").map((entry) => ({ ...entry, configuration_source: "stock_item" })),
    ...(resources.extras || []).filter((entry) => entry.track_stock && entry.strategy !== "recipe" && String(entry.inventory_kind || "").toLowerCase() === "resold").map((entry) => ({ ...entry, configuration_source: "sold_option" })),
  ].sort((left, right) => String(left.name || "").localeCompare(String(right.name || ""), "fr"));
  const openResoldEntry = (entry) => {
    if (entry.configuration_source !== "sold_option") {
      closeAnd(() => onStockItem(entry, false, entry.type));
      return;
    }
    // Always pass through the idempotent server operation, including for old
    // direct links, so they receive the unique technical identity before the
    // normal stock editor opens.
    onExtra(entry);
  };
  const renderRows = (rows, render, empty = "Aucun élément") => rows.filter(matches).length
    ? <div className="wv2-list">{rows.filter(matches).map(render)}</div>
    : <Empty title={empty} note="Ajoutez le premier élément ou modifiez la recherche." />;

  return <WaiseDrawer title={titles[panel] || "Configuration"} eyebrow="Réglages Waise" onClose={onClose} wide>
    {panel==='resold' && canConfigure ? <section className="wv2-drawer-block"><h3>Suivi des Extras revendus</h3>{(resources.extras || []).filter(extra=>!extra.recipe_id && extra.strategy!=='recipe').map(extra=><div className="wv2-row" key={extra.id}><span>{extra.name}</span><button type="button" className="wv2-secondary" disabled={Boolean(ensuringExtraId) || !commandAvailable(extra.track_stock ? 'extra.stock-tracking' : 'extra.ensure-resold-stock-item',Boolean(extra.track_stock))} onClick={()=>extra.track_stock ? onExtraTracking(extra) : onExtra(extra)}>{extra.track_stock ? 'Désactiver' : 'Activer'}</button></div>)}</section> : null}
    <div className="wv2-toolbar"><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher…" /></div>
    {panel === "warehouses" ? <>{canConfigure ? <ActionButton onClick={() => closeAnd(() => onAction({ type: "warehouse", record: null }))}>Nouveau lieu</ActionButton> : null}{renderRows(resources.warehouses || [], (entry) => <button type="button" className="wv2-row wv2-row--button" key={entry.id} onClick={() => canConfigure && closeAnd(() => onAction({ type: "warehouse", record: entry }))}><div className="wv2-row__main"><strong>{entry.name}</strong><small>{entry.is_main ? "Lieu principal" : entry.location || "Emplacement à compléter"} · {entry.items_managed} produit(s)</small></div>{entry.is_main ? <Badge status="ok">Principal</Badge> : <ChevronRight size={18} />}</button>)}</> : null}
    {panel === "suppliers" ? <>{canConfigure ? <ActionButton onClick={() => closeAnd(() => onAction({ type: "supplier", record: null }))}>Nouveau fournisseur</ActionButton> : null}{renderRows(resources.suppliers || [], (entry) => <button type="button" className="wv2-row wv2-row--button" key={entry.id} onClick={() => canConfigure && closeAnd(() => onAction({ type: "supplier", record: entry }))}><div className="wv2-row__main"><strong>{entry.name}</strong><small>{entry.contact_person || "Contact à compléter"} · {entry.associated_items} article(s)</small></div>{entry.open_orders ? <Badge status="expected">{entry.open_orders} en cours</Badge> : <ChevronRight size={18} />}</button>)}</> : null}
    {panel === "recipes" ? <>
      {data?.permissions?.can_manage_recipes ? <ActionButton onClick={() => closeAnd(() => onAction({ type: "recipe", record: null }))}>Nouvelle recette</ActionButton> : null}
      <section className="wv2-drawer-block"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Produits et déclinaisons</p><h3>Recettes de vente</h3></div></div>{renderRows(resources.recipes || [], (entry) => <button type="button" className="wv2-row wv2-row--button" key={entry.id} onClick={() => closeAnd(() => onAction({ type: "recipe", record: entry }))}><div className="wv2-row__main"><strong>{entry.name}</strong><small>{entry.variation_name ? `Déclinaison ${entry.variation_name} · ` : ""}{entry.ingredient_count + entry.consumable_count + entry.resold_count} composant(s)</small></div>{entry.cost_per_serving != null ? <div className="wv2-row__amount">{formatMoney(entry.cost_per_serving)}</div> : <Badge status="low_stock">Coût incomplet</Badge>}</button>, "Aucune recette")}</section>
    </> : null}
    {panel === "ingredients" ? <>
      {canConfigure ? <section className="wv2-drawer-block"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Matières internes</p><h3>Que voulez-vous ajouter ?</h3></div></div><div className="wv2-actions"><ActionButton onClick={() => closeAnd(() => onStockItem(null, false, "ingredient"))}>Ajouter un ingrédient</ActionButton><ActionButton onClick={() => closeAnd(() => onStockItem(null, false, "consumable"))}>Ajouter un consommable</ActionButton></div></section> : null}
      <section className="wv2-drawer-block"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Stock interne</p><h3>Ingrédients et consommables</h3></div></div>{renderRows((resources.stock_items || []).filter((entry) => !entry.product_id && !entry.extra_id), (entry) => <button type="button" className="wv2-row wv2-row--button" key={entry.id} onClick={() => canConfigure && closeAnd(() => onStockItem(entry, false, entry.type))}><div className="wv2-row__main"><strong>{entry.name}</strong><small>{entry.type === "consumable" ? "Consommable" : "Ingrédient"} · {entry.warehouse_name || "Lieu manquant"} · {entry.unit_symbol || "Unité manquante"}</small></div><ChevronRight size={18} /></button>, "Aucun ingrédient ou consommable")}</section>
    </> : null}
    {panel === "resold" ? <section className="wv2-drawer-block"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Catalogue MOO Sync</p><h3>Produits vendus suivis</h3></div></div><div className="wv2-drawer-note wv2-drawer-note--list-intro">Créez les produits vendus dans le tableau de bord MOO Sync, puis activez leur suivi de stock. Ils apparaîtront automatiquement ici.</div>{renderRows(resoldEntries, (entry) => <button type="button" className="wv2-row wv2-row--button" key={`${entry.configuration_source}:${entry.id}`} disabled={entry.configuration_source === "sold_option" && Number(ensuringExtraId) === Number(entry.id)} onClick={() => canConfigure && openResoldEntry(entry)}><div className="wv2-row__main"><strong>{entry.name}</strong><small>{entry.configuration_source === "sold_option" ? `Extra · ${entry.direct_links?.[0]?.warehouse_name || "Lieu à configurer"}` : `Produit de caisse · ${entry.warehouse_name || "Lieu manquant"}${entry.variation_id ? " · Déclinaison suivie" : ""}`}</small></div>{entry.configuration_source === "sold_option" && Number(ensuringExtraId) === Number(entry.id) ? <Loader2 size={18} className="is-spinning" /> : entry.configuration_source === "sold_option" && entry.status === "needs_configuration" ? <Badge status="low_stock">À configurer</Badge> : <ChevronRight size={18} />}</button>, "Aucun produit revendu suivi")}</section> : null}
    {panel === "losses" ? <><section className="wv2-drawer-block"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Classement</p><h3>Types de pertes</h3></div>{canConfigure ? <button type="button" className="wv2-secondary" onClick={() => closeAnd(() => onAction({ type: "loss_type", record: null }))}>Ajouter</button> : null}</div>{renderRows(resources.loss_types || [], (entry) => <button type="button" className="wv2-row wv2-row--button" key={entry.id} onClick={() => canConfigure && closeAnd(() => onAction({ type: "loss_type", record: entry }))}><span className="wv2-dot" style={{ background: entry.color || "#FFB800" }} /><div className="wv2-row__main"><strong>{entry.name}</strong><small>{entry.description || "Sans description"}</small></div><ChevronRight size={18} /></button>, "Aucun type de perte")}</section><section className="wv2-drawer-block"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Origine</p><h3>Causes de pertes</h3></div>{canConfigure ? <button type="button" className="wv2-secondary" onClick={() => closeAnd(() => onAction({ type: "loss_cause", record: null }))}>Ajouter</button> : null}</div>{renderRows(resources.loss_causes || [], (entry) => <button type="button" className="wv2-row wv2-row--button" key={entry.id} onClick={() => canConfigure && closeAnd(() => onAction({ type: "loss_cause", record: entry }))}><div className="wv2-row__main"><strong>{entry.name}</strong><small>{entry.category || entry.description || "Cause opérationnelle"}</small></div><ChevronRight size={18} /></button>, "Aucune cause de perte")}</section></> : null}
  </WaiseDrawer>;
};

const ConfigurationView = ({ data, onAction, onRefresh, requestedPanel, dailySituationConfig, onDailySituationToggle }) => {
  const readiness = data?.readiness || {};
  const [panel, setPanel] = useState(null);
  const [stockEditor, setStockEditor] = useState(null);
  const [ensuringExtraId, setEnsuringExtraId] = useState(null);
  useEffect(() => {
    if (requestedPanel?.id) setPanel(requestedPanel.id);
  }, [requestedPanel]);
  return <>
    <section className="wv2-pulse wv2-pulse--configuration"><div className="wv2-pulse__ring"><span /></div><div><p className="wv2-eyebrow">Centre de préparation</p><h2>{readiness.verdict || "Configuration de Waise"}</h2><p>{readiness.message || "Une configuration complète rend les analyses plus fiables."}</p></div></section>
    <div className="wv2-kpis"><Kpi label="Produits suivis" value={data?.summary?.tracked_products || 0} /><Kpi label="Lieux actifs" value={data?.summary?.warehouses || 0} /><Kpi label="Recettes" value={data?.summary?.recipes || 0} /><Kpi label="Réglages à finir" value={readiness.issue_count || 0} tone={readiness.issue_count ? "warning" : "success"} /></div>
    {data?.permissions?.can_configure_stock && dailySituationConfig ? <section className="wv2-section"><div className="wv2-section__head"><div><p className="wv2-eyebrow">Suivi</p><h3>Situation journalière</h3></div><button type="button" className={dailySituationConfig.enabled ? "wv2-secondary" : "wv2-primary"} disabled={!commandAvailable("daily-situation.config",true)} onClick={() => onDailySituationToggle?.(!dailySituationConfig.enabled)}>{dailySituationConfig.enabled ? "Désactiver" : "Activer"}</button></div></section> : null}
    <section className="wv2-grid wv2-grid--config">{(data?.checks || []).map((check) => <button type="button" className="wv2-card wv2-config-card" key={check.id} onClick={() => setPanel(check.id)}><div className="wv2-card__head"><div><small>Configuration</small><h3>{check.label}</h3></div><Badge status={check.complete ? "ok" : "low_stock"}>{check.complete ? "Prêt" : "À compléter"}</Badge></div><p>{check.detail}</p><span className="wv2-config-card__action">Ouvrir <ChevronRight size={17} /></span></button>)}</section>
    {panel ? <ConfigurationPanelDrawer panel={panel} data={data} onClose={() => setPanel(null)} onAction={onAction} onStockItem={(record, costsOnly, initialType) => setStockEditor({ record, costsOnly, initialType })} ensuringExtraId={ensuringExtraId} onExtra={async (extra) => {
      if (ensuringExtraId) return;
      setEnsuringExtraId(extra.id);
      try {
        const result = await mutateWaise(`/api/waise/extras/${extra.id}/ensure-resold-stock-item`, { method: "POST",source:(data?.resources?.extras || []).find(item=>item.id===extra.id) || extra, body: {} });
        setStockEditor({ record: { id: result.stock_item_id, name: extra.name, extra_id: extra.id }, costsOnly: false, initialType: "ingredient" });
        onRefresh?.();
      } catch (ensureError) {
        showWaiseToast("error", ensureError.message);
      } finally {
        setEnsuringExtraId(null);
      }
    }} onExtraTracking={async extra=>{
      if(ensuringExtraId) return;
      setEnsuringExtraId(extra.id);
      try {await mutateWaise(`/api/waise/extras/${extra.id}/stock-tracking`,{method:'PUT',source:extra,body:{strategy:'none'}});onRefresh?.();}
      catch(error) {showWaiseToast('error',error.message);}
      finally {setEnsuringExtraId(null);}
    }} /> : null}
    {stockEditor ? <StockItemConfigurationDrawer record={stockEditor.record} costsOnly={stockEditor.costsOnly} initialType={stockEditor.initialType} resources={data?.resources || {}} onClose={() => setStockEditor(null)} onDone={() => { setStockEditor(null); onRefresh?.(); }} /> : null}
  </>;
};

const VIEW_BY_SECTION = {
  today: TodayView,
  situation: DailySituationView,
  stock: StockView,
  purchases: PurchasesView,
  inventories: InventoriesView,
  losses: LossesView,
  analysis: AnalysisView,
  configuration: ConfigurationView,
};

const WaiseV2Interface = ({ currentUser: userProp }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [section, setSection] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [refreshToken, setRefreshToken] = useState(0);
  const [action, setAction] = useState(null);
  const [inventoryId, setInventoryId] = useState(null);
  const [inventoryFocusItemId, setInventoryFocusItemId] = useState(null);
  const [purchaseToReceive, setPurchaseToReceive] = useState(null);
  const [analysisFilters, setAnalysisFilters] = useState({ period_days: "30", start_date: "", end_date: "", warehouse_id: "", category: "", stock_item_id: "" });
  const [cloudSyncStatus, setCloudSyncStatus] = useState(null);
  const [configurationExpanded, setConfigurationExpanded] = useState(false);
  const [configurationPanelRequest, setConfigurationPanelRequest] = useState(null);
  const [dailySituationConfig, setDailySituationConfig] = useState(null);
  const [dailyDate, setDailyDate] = useState("");
  const requestRef = useRef(0);
  const realtimeRefreshRef = useRef(null);
  const currentUser = userProp || safeJson(localStorage.getItem("moo_cloud_user") || localStorage.getItem("moo_user"), {});
  const visibleSections = useMemo(() => SECTIONS.filter((entry) => !entry.requiresDailySituation || dailySituationConfig?.enabled), [dailySituationConfig?.enabled]);
  const sectionInfo = useMemo(() => visibleSections.find((entry) => entry.id === section) || visibleSections[0], [section, visibleSections]);
  const sectionEndpoint = useMemo(() => {
    if (section === "situation") return `${sectionInfo.endpoint}${dailyDate ? `?date=${encodeURIComponent(dailyDate)}` : ""}`;
    if (section !== "analysis") return sectionInfo.endpoint;
    const params = new URLSearchParams();
    Object.entries(analysisFilters).forEach(([key, value]) => { if (value) params.set(key, value); });
    return `${sectionInfo.endpoint}?${params.toString()}`;
  }, [analysisFilters, dailyDate, section, sectionInfo.endpoint]);
  const dashboardPath = location.pathname.startsWith("/superadmin") ? "/superadmin/dashboard" : location.pathname.startsWith("/admin") ? "/admin/dashboard" : "/";

  const load = useCallback(() => setRefreshToken((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    fetchWaise("/api/waise/v2/daily-situation/config")
      .then((config) => active && setDailySituationConfig(config))
      .catch(() => active && setDailySituationConfig({ enabled: false }));
    return () => { active = false; };
  }, [refreshToken]);

  useEffect(() => {
    if (section === "situation" && !dailySituationConfig?.enabled) setSection("today");
  }, [dailySituationConfig?.enabled, section]);

  const toggleDailySituation = useCallback(async (enabled) => {
    try {
      const config = await mutateWaise("/api/waise/v2/daily-situation/config", { method: "PUT",source:dailySituationConfig, body: { enabled } });
      setDailySituationConfig(config);
      showWaiseToast("success", enabled ? "Situation journalière activée." : "Situation journalière désactivée.");
      load();
    } catch (toggleError) {
      showWaiseToast("error", toggleError.message);
    }
  }, [load,dailySituationConfig]);

  useEffect(() => {
    const socket = io({ transports: ["websocket", "polling"] });
    const scheduleRefresh = () => {
      window.clearTimeout(realtimeRefreshRef.current);
      realtimeRefreshRef.current = window.setTimeout(load, 250);
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") scheduleRefresh();
    };
    socket.on("waiseStockChanged", scheduleRefresh);
    socket.on("waiseLossDeclarationChanged", scheduleRefresh);
    socket.on("waiseConfigurationChanged", scheduleRefresh);
    socket.on("stockMovementCreated", scheduleRefresh);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("waise-cloud-command-resolved", scheduleRefresh);
    return () => {
      window.clearTimeout(realtimeRefreshRef.current);
      socket.off("waiseStockChanged", scheduleRefresh);
      socket.off("waiseLossDeclarationChanged", scheduleRefresh);
      socket.off("waiseConfigurationChanged", scheduleRefresh);
      socket.off("stockMovementCreated", scheduleRefresh);
      socket.disconnect();
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("waise-cloud-command-resolved", scheduleRefresh);
    };
  }, [load]);

  useEffect(() => {
    let active = true;
    const refreshCloudStatus = () => {
      fetchWaise("/api/waise/cloud-sync-status")
        .then((status) => active && setCloudSyncStatus(status))
        .catch(() => active && setCloudSyncStatus(null));
    };
    refreshCloudStatus();
    const timer = window.setInterval(refreshCloudStatus, 15000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = ++requestRef.current;
    const timeout = setTimeout(() => controller.abort(), 8000);
    setLoading(true);
    setError("");
    fetchWaise(sectionEndpoint, controller.signal)
      .then((payload) => {
        if (requestRef.current !== requestId) return;
        if (!isUsableWaiseSectionPayload(section, payload)) {
          throw new Error("Réponse WAISE incomplète. Les dernières données affichées sont conservées.");
        }
        setData(payload);
      })
      .catch((loadError) => {
        if (requestRef.current !== requestId || loadError.name === "AbortError") {
          if (loadError.name === "AbortError" && requestRef.current === requestId) setError("Waise met trop de temps à répondre. La caisse reste disponible.");
          return;
        }
        setError(loadError.message);
      })
      .finally(() => {
        clearTimeout(timeout);
        if (requestRef.current === requestId) setLoading(false);
      });
    return () => { clearTimeout(timeout); controller.abort(); };
  }, [section, sectionEndpoint, refreshToken]);

  const View = VIEW_BY_SECTION[section] || TodayView;
  const displayName = currentUser?.first_name || currentUser?.username || "Utilisateur";
  const actionType = typeof action === "string" ? action : action?.type;
  const actionSeedItemId = typeof action === "object" ? action?.seedItemId : null;
  const actionRecord = typeof action === "object" ? action?.record : null;

  return (
    <div className="wv2-shell">
      <aside className="wv2-nav">
        <div className="wv2-brand"><span>W</span><div><strong>Waise</strong><small>MOO Sync</small></div></div>
        <nav aria-label="Sections de Waise V2">{visibleSections.map(({ id, label, icon: Icon }) => <React.Fragment key={id}><button type="button" className={section === id ? "is-active" : ""} onClick={() => { setSection(id); setQuery(""); if (id === "configuration") setConfigurationExpanded((value) => !value); }}><Icon size={19} /><span>{label}</span>{id === "configuration" ? <ChevronRight size={15} className={configurationExpanded ? "is-rotated" : ""} /> : null}</button>{id === "configuration" && configurationExpanded ? <div className="wv2-nav__subsections">{CONFIGURATION_SUBSECTIONS.map((entry) => <button type="button" key={entry.id} onClick={() => { setSection("configuration"); setConfigurationPanelRequest({ id: entry.id, nonce: Date.now() }); }}>{entry.label}</button>)}</div> : null}</React.Fragment>)}</nav>
        <button type="button" className="wv2-back" onClick={() => navigate(dashboardPath)}><ArrowLeft size={18} /><span>Retour à MOO Sync</span></button>
      </aside>
      <main className="wv2-main">
        <header className="wv2-header"><div><p className="wv2-eyebrow">{sectionInfo.label}</p><h1>{section === "today" ? `Bonjour ${displayName}` : sectionInfo.label}</h1><p className="wv2-fresh">{isRemoteCloudHost(window.location?.hostname) ? "Données synchronisées" : "Données locales"} · {data?.generated_at ? new Date(data.generated_at).toLocaleString("fr-FR") : "chargement en cours"}</p>{cloudSyncStatus ? <p className="wv2-cloud-state"><span className={cloudSyncStatus.local_server_connected ? "is-online" : "is-offline"} />{cloudSyncStatus.local_server_connected ? "Serveur local connecté" : "Serveur local hors ligne"}{cloudSyncStatus.pending_commands ? ` · ${cloudSyncStatus.pending_commands} action(s) en attente` : ""}{cloudSyncStatus.pending_events ? ` · ${cloudSyncStatus.pending_events} mise(s) à jour à envoyer` : ""}</p> : null}</div><button type="button" className="wv2-refresh" onClick={load} disabled={loading}><RefreshCw size={18} className={loading ? "is-spinning" : ""} /> Actualiser</button></header>
        {error ? <div className="wv2-error" role="alert"><WifiOff size={20} /><div><strong>Waise est momentanément indisponible</strong><span>{error}</span><small>La caisse, les paiements et Cuisine/Bar continuent normalement.</small></div><button type="button" onClick={load}><RotateCcw size={16} /> Réessayer</button></div> : null}
        {loading && !data ? <div className="wv2-loading"><Loader2 className="is-spinning" size={30} /><span>Waise prépare les informations…</span></div> : <View data={data || {}} query={query} setQuery={setQuery} analysisFilters={analysisFilters} setAnalysisFilters={setAnalysisFilters} dailyDate={dailyDate} setDailyDate={setDailyDate} dailySituationConfig={dailySituationConfig} onDailySituationToggle={toggleDailySituation} requestedPanel={configurationPanelRequest} onSelect={setSection} onAction={setAction} onRefresh={load} onOpenInventory={(id) => { setInventoryFocusItemId(null); setInventoryId(id); }} onReceivePurchase={setPurchaseToReceive} navigate={navigate} />}
      </main>
      <nav className="wv2-mobile-nav" aria-label="Navigation Waise mobile" style={{ gridTemplateColumns: `repeat(${visibleSections.length}, minmax(0, 1fr))` }}>{visibleSections.map(({ id, label, mobileLabel, icon: Icon }) => <button type="button" key={id} className={section === id ? "is-active" : ""} onClick={() => setSection(id)}><Icon size={18} /><span>{mobileLabel || label}</span></button>)}</nav>
      {actionType === "purchase" ? <PurchaseOrderDrawer seedItemId={actionSeedItemId} onClose={() => setAction(null)} onSaved={() => load()} /> : null}
      {actionType === "recipe" ? <RecipeDrawer seedRecipe={actionRecord} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} /> : null}
      {["warehouse", "supplier"].includes(actionType) ? <ConfigurationResourceDrawer type={actionType} record={actionRecord} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} /> : null}
      {["loss_type", "loss_cause"].includes(actionType) ? <LossConfigDrawer type={actionType} record={actionRecord} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} /> : null}
      {actionType && !["purchase", "recipe", "warehouse", "supplier", "loss_type", "loss_cause"].includes(actionType) ? <WaiseActionDrawer type={actionType} seedItemId={actionSeedItemId} onClose={() => setAction(null)} onDone={(result) => { const completedAction = actionType; const completedSeedItemId = actionSeedItemId; setAction(null); load(); if (completedAction === "inventory" && result?.id) { setInventoryFocusItemId(completedSeedItemId || null); setInventoryId(result.id); } }} /> : null}
      {inventoryId ? <InventoryDialog inventoryId={inventoryId} focusItemId={inventoryFocusItemId} canFinalize={Boolean(data?.permissions?.can_review_inventory_variances)} onClose={() => { setInventoryId(null); setInventoryFocusItemId(null); }} onDone={() => { setInventoryId(null); setInventoryFocusItemId(null); load(); }} /> : null}
      {purchaseToReceive ? <ReceivePurchaseDialog order={purchaseToReceive} onClose={() => setPurchaseToReceive(null)} onDone={() => { setPurchaseToReceive(null); load(); }} /> : null}
    </div>
  );
};

export default WaiseV2Interface;
export { InventoryDialog, WaiseActionDrawer, ConfigurationResourceDrawer, StockItemConfigurationDrawer };
